// src/pages/account/creator/courses/CourseEditor.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Spinner,
  Alert,
  ProgressBar,
  Modal,
} from "react-bootstrap";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import * as tus from "tus-js-client";

import { app, db, storage } from "../../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../../shared/auth/AuthProvider";
import {
  DEFAULT_AFFILIATE_COURSE_SETTINGS,
  normalizeAffiliateCommissionPercent,
} from "../../../../shared/utils/affiliateSchema";

import "./CourseEditor.css";
import CourseReviewSettings from "./CourseReviewSettings";
import {
  COURSE_EDITOR_MUX_STATE,
  CONTROLLED_MUX_BATCH_LIMIT,
  getCourseEditorMuxState,
  isExactCourseVideoDeleteConfirmation,
  shouldObserveCourseEditorVideo,
  shouldPollCourseVideoDeletion,
  shouldPollCoursePreparation,
  shouldShowCourseStructurePlaceholder,
  startCoursePreparationPolling,
  standbyPolicyUpdateErrorMessage,
  toggleControlledMuxLessonSelection,
} from "./courseEditorMuxRefresh";

const CATEGORY_OPTIONS = [
  "เนติบัณฑิต",
  "ทนายความ",
  "อัยการ",
  "ผู้พิพากษา",
  "ก.พ.",
];

const LEVEL_OPTIONS = ["beginner", "intermediate", "advanced"];

// VIDEO-DR.6.5B.DELETE-UI-DEFER: keep the hardened backend deletion paths
// available for a later product phase, but do not expose destructive controls.
const VIDEO_DELETION_CONTROLS_VISIBLE = false;

const COURSE_TYPE_OPTIONS = [
  { value: "thaibar", label: "เนติบัณฑิต" },
  { value: "lawyer", label: "ทนายความ" },
  { value: "attorney", label: "อัยการ" },
  { value: "judge", label: "ผู้พิพากษา" },
  { value: "cse", label: "ก.พ." },
];

const THAIBAR_HEADER_TYPES = [
  { value: "civil", label: "คอร์สยาว ขาแพ่ง/วิแพ่ง (civil)" },
  { value: "criminal", label: "คอร์สยาว ขาอาญา/วิอาญา (criminal)" },
  { value: "summary_civil", label: "คอร์สสรุป ขาแพ่ง/วิแพ่ง (summary civil)" },
  { value: "summary_criminal", label: "คอร์สสรุป ขาอาญา/วิอาญา (summary criminal)" },
];

const THAIBAR_PACKAGE_OPTIONS = [
  { value: "F_2", label: "การันตี (ยาว+สรุป) 2 ขา" },
  { value: "F_1_civil", label: "การันตี (ยาว+สรุป) ขาแพ่ง/วิแพ่ง 1 ขา" },
  { value: "F_1_criminal", label: "การันตี (ยาว+สรุป) ขาอาญา/วิอาญา 1 ขา" },

  { value: "P_2", label: "เพอร์เฟค (ยาว+สรุป+ประมวล) 2 ขา" },
  { value: "P_1_civil", label: "เพอร์เฟค (ยาว+สรุป+ประมวล) ขาแพ่ง/วิแพ่ง 1 ขา" },
  { value: "P_1_criminal", label: "เพอร์เฟค (ยาว+สรุป+ประมวล) ขาอาญา/วิอาญา 1 ขา" },

  { value: "L_2", label: "เนติบัณฑิต (ยาว) 2 ขา" },
  { value: "L_1_civil", label: "เนติบัณฑิต (ยาว) ขาแพ่ง/วิแพ่ง 1 ขา" },
  { value: "L_1_criminal", label: "เนติบัณฑิต (ยาว) ขาอาญา/วิอาญา 1 ขา" },

  { value: "S_2", label: "เนติบัณฑิต (สรุป) 2 ขา" },
  { value: "S_1_civil", label: "เนติบัณฑิต (สรุป) ขาแพ่ง/วิแพ่ง 1 ขา" },
  { value: "S_1_criminal", label: "เนติบัณฑิต (สรุป) ขาอาญา/วิอาญา 1 ขา" },
];


const COURSE_DOWNLOAD_SCOPE_OPTIONS = [
  { value: "general", label: "เอกสารทั่วไป (เปิดดูได้ทุกคนที่มีสิทธิเข้าคอร์ส)" },
  { value: "long_civil", label: "ยาวแพ่ง/วิแพ่ง" },
  { value: "long_criminal", label: "ยาวอาญา/วิอาญา" },
  { value: "summary_civil", label: "สรุปแพ่ง/วิแพ่ง" },
  { value: "summary_criminal", label: "สรุปอาญา/วิอาญา" },
];

const SPECIAL_COURSE_DOWNLOADS = [
  {
    scope: "statute_civil",
    heading: "มาตราสำคัญ (แพ่ง/วิแพ่ง)",
    folder: "statute-civil",
  },
  {
    scope: "statute_criminal",
    heading: "มาตราสำคัญ (อาญา/วิอาญา)",
    folder: "statute-criminal",
  },
];

const SPECIAL_COURSE_DOWNLOAD_SCOPES = new Set(
  SPECIAL_COURSE_DOWNLOADS.map((item) => item.scope)
);

const THAIBAR_HEADER_DEFAULT_TITLE_MAP = {
  civil: "เนติบัณฑิตขาแพ่ง/วิแพ่ง",
  criminal: "เนติบัณฑิตขาอาญา/วิอาญา",
  summary_civil: "สรุปขาแพ่ง/วิแพ่ง",
  summary_criminal: "สรุปขาอาญา/วิอาญา",
};

const EMPTY_THAIBAR_PACKAGE_PRICES = THAIBAR_PACKAGE_OPTIONS.reduce((acc, item) => {
  acc[item.value] = { price: "", priceText: "" };
  return acc;
}, {});

function slugifyTH(s = "") {
  return String(s)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9ก-๙\-]/g, "")
    .replace(/\-+/g, "-")
    .slice(0, 80);
}

function cleanLinesToArray(text = "") {
  return String(text)
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);
}

function arrayToLines(arr) {
  if (!Array.isArray(arr)) return "";
  return arr.filter(Boolean).join("\n");
}

function toDateInputValue(value) {
  try {
    if (!value) return "";
    if (typeof value === "string") return value.slice(0, 10);

    if (value?.toDate) {
      const d = value.toDate();
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    return "";
  } catch {
    return "";
  }
}

function fromDateInputValue(value) {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return Timestamp.fromDate(d);
}

function formatThaiDate(value) {
  try {
    if (!value) return "";
    const d = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function formatBytes(bytes = 0) {
  if (!bytes || Number.isNaN(Number(bytes))) return "0 B";
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function makeEmptyCourseDownload() {
  return {
    id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    scope: "general",
    name: "",
    fileUrl: "",
    storagePath: "",
    fileName: "",
    ext: "",
    size: 0,
    uploadedAt: "",
    isActive: true,
  };
}

function normalizeCourseDownloads(items) {
  return safeArray(items)
    .filter((x) => x?.name?.trim() || x?.fileUrl)
    .map((x) => ({
      id: x.id || `doc_${Date.now()}`,
      scope: x.scope || "general",
      name: String(x.name || "").trim(),
      fileUrl: String(x.fileUrl || "").trim(),
      storagePath: String(x.storagePath || "").trim(),
      fileName: String(x.fileName || "").trim(),
      ext: String(x.ext || "").trim(),
      size: Number(x.size || 0),
      uploadedAt: x.uploadedAt || new Date().toISOString(),
      isActive: x.isActive !== false,
    }));
}

function getUploadedAtMillis(value) {
  try {
    const date = value?.toDate ? value.toDate() : new Date(value || "");
    const millis = date.getTime();
    return Number.isFinite(millis) ? millis : -1;
  } catch {
    return -1;
  }
}

function getSpecialCourseDownload(items, scope) {
  return safeArray(items)
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item?.scope === scope)
    .sort((a, b) => {
      return (
        getUploadedAtMillis(b.item?.uploadedAt) -
          getUploadedAtMillis(a.item?.uploadedAt) ||
        a.index - b.index
      );
    })[0]?.item || null;
}

function makeEmptyCourseImage() {
  return {
    id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name: "",
    imageUrl: "",
    storagePath: "",
    fileName: "",
    size: 0,
    uploadedAt: "",
    isActive: true,
  };
}

function isVideoReady(video = {}) {
  return !!video?.isReady || !!video?.readyToStream || video?.status === "ready";
}

function getCloudflareCurrentForEditor(video = {}) {
  const current = video?.providers?.cloudflare?.current;
  if (current?.assetId) return current;
  const assetId = video?.uid || video?.streamUid || "";
  return assetId
    ? {
        assetId,
        generation: 0,
        status: video?.status || "",
        isReady: isVideoReady(video),
      }
    : null;
}

function getCloudflareCandidateForEditor(video = {}) {
  return video?.providers?.cloudflare?.candidate || null;
}

function getMuxStandbyStatus(video = {}) {
  return ({
    [COURSE_EDITOR_MUX_STATE.MATCHING]: "วิดีโอสำรองพร้อมใช้แล้ว",
    [COURSE_EDITOR_MUX_STATE.PROCESSING]: "กำลังเตรียม",
    [COURSE_EDITOR_MUX_STATE.ERROR]: "ผิดพลาด",
    [COURSE_EDITOR_MUX_STATE.STALE]: "ต้องอัปเดต",
    [COURSE_EDITOR_MUX_STATE.HELD_FOR_PRIMARY]: "รอวิดีโอหลักพร้อม",
    [COURSE_EDITOR_MUX_STATE.MISSING]: "ยังไม่ได้เตรียม",
  })[getCourseEditorMuxState(video)];
}

function getMuxStandbyVariant(video = {}) {
  const state = getCourseEditorMuxState(video);
  if (state === COURSE_EDITOR_MUX_STATE.MATCHING) return "success";
  if (state === COURSE_EDITOR_MUX_STATE.ERROR) return "danger";
  if ([COURSE_EDITOR_MUX_STATE.PROCESSING, COURSE_EDITOR_MUX_STATE.STALE,
    COURSE_EDITOR_MUX_STATE.HELD_FOR_PRIMARY].includes(state)) return "warning";
  return "secondary";
}

function getLessonContentStatus({ releaseAt, video, manualPublished = false }) {
  if (manualPublished) return "published";

  const ready = isVideoReady(video);
  const releaseDate = releaseAt?.toDate ? releaseAt.toDate() : releaseAt ? new Date(releaseAt) : null;
  const now = new Date();

  if (!ready) return releaseDate ? "scheduled" : "draft";
  if (!releaseDate) return "draft";
  if (releaseDate.getTime() <= now.getTime()) return "published";
  return "scheduled";
}

function getThaibarPackageLabel(pkgValue) {
  return (
    THAIBAR_PACKAGE_OPTIONS.find((x) => x.value === pkgValue)?.label || pkgValue
  );
}

export default function CourseEditor() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const isEdit = useMemo(() => !!courseId && courseId !== "new", [courseId]);

  const { user, userDoc, loading } = useAuth();
  const roles = userDoc?.role;

  const functions = useMemo(() => getFunctions(app, "asia-southeast1"), []);

  const [loadingPage, setLoadingPage] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingDocKey, setUploadingDocKey] = useState("");
  const [videoUploadingMap, setVideoUploadingMap] = useState({});
  const [videoUploadModeMap, setVideoUploadModeMap] = useState({});
  const videoUploadFileRef = useRef({});
  const [deletingVideoMap, setDeletingVideoMap] = useState({});
  const [err, setErr] = useState("");
  const [muxPreparation, setMuxPreparation] = useState(null);
  const [muxPreparationBusy, setMuxPreparationBusy] = useState(false);
  const [standbyAvailability, setStandbyAvailability] = useState("disabled");
  const [standbyPolicyBusy, setStandbyPolicyBusy] = useState(false);
  const [courseVideoDeletion, setCourseVideoDeletion] = useState(null);
  const [courseVideoDeletionLoaded, setCourseVideoDeletionLoaded] = useState(false);
  const [courseVideoDeletionBusy, setCourseVideoDeletionBusy] = useState(false);
  const [showCourseVideoDeletionModal, setShowCourseVideoDeletionModal] = useState(false);
  const [courseVideoDeletionConfirmation, setCourseVideoDeletionConfirmation] = useState("");
  const [controlledMuxLessons, setControlledMuxLessons] = useState([]);
  const [controlledMuxBusy, setControlledMuxBusy] = useState(false);

  const [title, setTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [slug, setSlug] = useState("");

  const [courseType, setCourseType] = useState("thaibar");
  const [categories, setCategories] = useState(["เนติบัณฑิต"]);
  const [level, setLevel] = useState("beginner");

  const [imageCoverUrl, setImageCoverUrl] = useState("");
  const [videoAdsUrl, setVideoAdsUrl] = useState("");

  const [price, setPrice] = useState(0);
  const [priceText, setPriceText] = useState("");
  const [packagePrices, setPackagePrices] = useState(EMPTY_THAIBAR_PACKAGE_PRICES);

  const [allowInstallment, setAllowInstallment] = useState(false);
  const [maxInstallments, setMaxInstallments] = useState(3);
  const [installmentPackageKeys, setInstallmentPackageKeys] = useState([]);

  const [affiliateEnabled, setAffiliateEnabled] = useState(
    DEFAULT_AFFILIATE_COURSE_SETTINGS.affiliateEnabled
  );
  const [affiliateCommissionPercent, setAffiliateCommissionPercent] = useState(
    DEFAULT_AFFILIATE_COURSE_SETTINGS.affiliateCommissionPercent
  );

  const [isActive, setIsActive] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);

  const [highlightsText, setHighlightsText] = useState("");
  const [courseDescriptions, setCourseDescriptions] = useState("");
  const [receiveText, setReceiveText] = useState("");

  const [headers, setHeaders] = useState([]);
  const [coverFile, setCoverFile] = useState(null);

  const [courseDownloads, setCourseDownloads] = useState([makeEmptyCourseDownload()]);
  const [uploadingCourseDocId, setUploadingCourseDocId] = useState("");
  const [courseCalendarImages, setCourseCalendarImages] = useState([makeEmptyCourseImage()]);
  const [courseScheduleImages, setCourseScheduleImages] = useState([makeEmptyCourseImage()]);
  const [courseChecklistImages, setCourseChecklistImages] = useState([makeEmptyCourseImage()]);
  const [uploadingCourseImageKey, setUploadingCourseImageKey] = useState("");

  const [showCourseCalendarImages, setShowCourseCalendarImages] = useState(true);
  const [showCourseScheduleImages, setShowCourseScheduleImages] = useState(true);
  const [showCourseChecklistImages, setShowCourseChecklistImages] = useState(true);

  const [lineGroupQrImage, setLineGroupQrImage] = useState({
    imageUrl: "",
    storagePath: "",
    fileName: "",
    size: 0,
    uploadedAt: "",
    isActive: true,
  });
  const [uploadingLineGroupQr, setUploadingLineGroupQr] = useState(false);

  const [showHeaderModal, setShowHeaderModal] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);

  const [pendingHeaderTitle, setPendingHeaderTitle] = useState("");
  const [pendingHeaderType, setPendingHeaderType] = useState("civil");

  const [pendingSectionTitle, setPendingSectionTitle] = useState("");
  const [targetHeaderIdForSection, setTargetHeaderIdForSection] = useState("");

  const [headerTitleTouched, setHeaderTitleTouched] = useState(false);

  const [showLessonModal, setShowLessonModal] = useState(false);
  const [pendingLessonTitle, setPendingLessonTitle] = useState("");
  const [targetHeaderIdForLesson, setTargetHeaderIdForLesson] = useState("");
  const [targetSectionIdForLesson, setTargetSectionIdForLesson] = useState("");

  const [showCurriculumModal, setShowCurriculumModal] = useState(false);
  const [openHeaderKey, setOpenHeaderKey] = useState("");
  const [openSectionKey, setOpenSectionKey] = useState("");

  const [expandedHeaders, setExpandedHeaders] = useState({});
  const [expandedSections, setExpandedSections] = useState({});

  const videoItemRealtimeRefs = useRef({});

  const canAccess = useMemo(() => {
    const arr = Array.isArray(roles) ? roles : [roles].filter(Boolean);
    const set = new Set(arr.map((x) => String(x).toLowerCase()));
    return set.has("admin") || set.has("creator");
  }, [roles]);

  const lockCourseTypeAndSlug = useMemo(() => isEdit, [isEdit]);
  const courseDeletionActive = shouldPollCourseVideoDeletion(courseVideoDeletion);
  const courseVideoInventory = useMemo(() => safeArray(headers).reduce((total, header) => {
    safeArray(header.sections).forEach((section) => safeArray(section.items).forEach((item) => {
      const video = item?.video || {}; const providers = video.providers || {};
      const cf = [providers.cloudflare?.current, providers.cloudflare?.candidate]
        .filter((asset) => asset?.assetId).length;
      const mux = [providers.mux?.current, providers.mux?.candidate]
        .filter((asset) => asset?.assetId).length;
      total.cloudflare += cf; total.mux += mux; if (cf + mux) total.lessons += 1;
    }));
    return total;
  }, { cloudflare: 0, mux: 0, lessons: 0 }), [headers]);

  const curriculumPreviewData = useMemo(() => {
    return safeArray(headers).map((h) => ({
      headerTitle: h?.title || "Header",
      headerType: h?.headerType || "",
      sections: safeArray(h?.sections).map((s) => ({
        title: s?.title || "Section",
        topics: safeArray(s?.items).map((it) => {
          const video = it?.video || {};
          const ready = isVideoReady(video);
          const published = it?.contentStatus === "published";
          const hasVideoUid = !!(video?.uid || video?.streamUid);

          let bulletStatus = "empty";
          if (ready && published) {
              bulletStatus = "published";
            } else if (ready && !published) {
              bulletStatus = "scheduled";
            } else if (!hasVideoUid) {
              bulletStatus = "empty";
            } else {
              bulletStatus = "empty";
          }

          const docs = safeArray(it?.documents);
          const hasPdfDocument = docs.some((doc) => {
            const ext = String(doc?.ext || "").toLowerCase();
            const fileName = String(doc?.name || "").toLowerCase();
            return ext === "pdf" || fileName.endsWith(".pdf");
          });

          return {
            title: it?.title || "-",
            releaseDateText: formatThaiFullDate(
              it?.releaseDateText || it?.releaseAt || ""
            ),
            bulletStatus,
            contentStatus: it?.contentStatus || "",
            videoStatus: video?.status || "",
            hasPdfDocument,
          };
        }),
      })),
    }));
  }, [headers]);

  function formatThaiFullDate(value) {
    try {
      if (!value) return "";

      let d = null;

      if (typeof value === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          d = new Date(`${value}T00:00:00`);
        } else {
          d = new Date(value);
        }
      } else if (value?.toDate) {
        d = value.toDate();
      } else if (value instanceof Date) {
        d = value;
      }

      if (!d || Number.isNaN(d.getTime())) return "";

      return d.toLocaleDateString("th-TH", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "";
    }
  }

  useEffect(() => {
    if (isEdit) return;
    if (!title) return;
    setSlug((prev) => (prev ? prev : slugifyTH(title)));
  }, [title, isEdit]);

  const toggleCategory = (c) => {
    setCategories((prev) => {
      const set = new Set(prev || []);
      if (set.has(c)) set.delete(c);
      else set.add(c);
      return Array.from(set);
    });
  };

  const updatePackagePriceField = (pkgKey, field, value) => {
    setPackagePrices((prev) => ({
      ...prev,
      [pkgKey]: {
        ...(prev?.[pkgKey] || {}),
        [field]: value,
      },
    }));
  };

  const toggleInstallmentPackageKey = (pkgValue) => {
    setInstallmentPackageKeys((prev) => {
      const set = new Set(safeArray(prev));
      if (set.has(pkgValue)) set.delete(pkgValue);
      else set.add(pkgValue);
      return Array.from(set);
    });
  };

  useEffect(() => {
    if (!user) return;

    (async () => {
      try {
        setErr("");
        setLoadingPage(true);

        if (!isEdit) {
          setLoadingPage(false);
          return;
        }

        const courseRef = doc(db, "courses", courseId);
        const snap = await getDoc(courseRef);

        if (!snap.exists()) {
          setErr("ไม่พบคอร์สนี้");
          setLoadingPage(false);
          return;
        }

        const data = snap.data();

        setTitle(data?.title || "");
        setSubTitle(data?.subTitle || "");
        setSlug(data?.slug || "");

        setCourseType(data?.courseType || "thaibar");
        setCategories(Array.isArray(data?.categories) ? data.categories : []);
        setLevel(data?.level || "beginner");

        setImageCoverUrl(data?.imageCoverUrl || data?.coverUrl || "");
        setVideoAdsUrl(data?.videoAdsUrl || "");

        setLineGroupQrImage(
          data?.lineGroupQrImage || {
            imageUrl: "",
            storagePath: "",
            fileName: "",
            size: 0,
            uploadedAt: "",
            isActive: true,
          }
        );

        setPrice(Number(data?.price || 0));
        setPriceText(data?.priceText || "");
        setPackagePrices({
          ...EMPTY_THAIBAR_PACKAGE_PRICES,
          ...(data?.packagePrices || {}),
        });

        setAllowInstallment(!!data?.allowInstallment);
        setMaxInstallments(Number(data?.maxInstallments || 3));
        setInstallmentPackageKeys(
          Array.isArray(data?.installmentPackageKeys)
            ? data.installmentPackageKeys
            : []
        );

        setAffiliateEnabled(
          data?.affiliateEnabled ??
            DEFAULT_AFFILIATE_COURSE_SETTINGS.affiliateEnabled
        );
        setAffiliateCommissionPercent(
          normalizeAffiliateCommissionPercent(
            data?.affiliateCommissionPercent,
            DEFAULT_AFFILIATE_COURSE_SETTINGS.affiliateCommissionPercent
          )
        );

        setIsActive(data?.isActive ?? true);
        setIsFeatured(data?.isFeatured ?? false);
        setStandbyAvailability(
          ["admin_preview_only", "learner_enabled"].includes(
            data?.videoStandbyPlayback?.availability
          )
            ? data.videoStandbyPlayback.availability
            : "disabled"
        );

        setHighlightsText(arrayToLines(data?.courseHighlights || []));
        setCourseDescriptions(data?.courseDescriptions || "");
        setReceiveText(arrayToLines(data?.courseReceive || []));

        setCourseDownloads(
          Array.isArray(data?.courseDownloads) && data.courseDownloads.length
            ? data.courseDownloads
            : [makeEmptyCourseDownload()]
        );

        setCourseCalendarImages(
          Array.isArray(data?.courseCalendarImages) && data.courseCalendarImages.length
            ? data.courseCalendarImages
            : [makeEmptyCourseImage()]
        );

        setCourseScheduleImages(
          Array.isArray(data?.courseScheduleImages) && data.courseScheduleImages.length
            ? data.courseScheduleImages
            : [makeEmptyCourseImage()]
        );

        setCourseChecklistImages(
          Array.isArray(data?.courseChecklistImages) && data.courseChecklistImages.length
            ? data.courseChecklistImages
            : [makeEmptyCourseImage()]
        );

        setShowCourseCalendarImages(data?.showCourseCalendarImages !== false);
        setShowCourseScheduleImages(data?.showCourseScheduleImages !== false);
        setShowCourseChecklistImages(data?.showCourseChecklistImages !== false);

        await loadStructure(courseId);
        setLoadingPage(false);
      } catch (e) {
        console.error(e);
        setErr(e?.message || "โหลดข้อมูลไม่สำเร็จ");
        setLoadingPage(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isEdit, courseId]);

  const loadStructure = async (cid, { background = false } = {}) => {
    if (!cid) return;

    if (!background) setLoadingStructure(true);
    try {
      const headerSnap = await getDocs(
        query(collection(db, "courses", cid, "headers"), orderBy("order", "asc"))
      );

      const hs = [];

      for (const h of headerSnap.docs) {
        const header = { id: h.id, ...h.data(), sections: [] };

        const secSnap = await getDocs(
          query(
            collection(db, "courses", cid, "headers", h.id, "sections"),
            orderBy("order", "asc")
          )
        );

        const secs = [];

        for (const s of secSnap.docs) {
          const sec = { id: s.id, ...s.data(), items: [] };

          const itemSnap = await getDocs(
            query(
              collection(db, "courses", cid, "headers", h.id, "sections", s.id, "items"),
              orderBy("order", "asc")
            )
          );

          sec.items = itemSnap.docs.map((x) => ({ id: x.id, ...x.data() }));
          secs.push(sec);
        }

        header.sections = secs;
        hs.push(header);
      }

      setHeaders(hs);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "โหลดโครงสร้างไม่สำเร็จ");
    } finally {
      if (!background) setLoadingStructure(false);
    }
  };

  const loadMuxPreparationStatus = async () => {
    if (!isEdit || !courseId) return;
    try {
      const callable = httpsCallable(functions, "getCourseMuxPreparationStatus");
      const response = await callable({ courseId });
      setMuxPreparation(response.data || null);
      if (["queued", "running"].includes(response.data?.job?.status)) {
        await loadStructure(courseId, { background: true });
      }
    } catch (error) {
      console.error("load Mux preparation status:", error);
    }
  };

  useEffect(() => {
    if (!isEdit || !courseId) return undefined;
    loadMuxPreparationStatus();
    return startCoursePreparationPolling({
      active: shouldPollCoursePreparation(muxPreparation?.job),
      refresh: loadMuxPreparationStatus,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, isEdit, muxPreparation?.job?.status]);

  const loadCourseVideoDeletionStatus = async () => {
    if (!isEdit || !courseId) return;
    try {
      const callable = httpsCallable(functions, "getCourseVideoDeletionStatus");
      const response = await callable({ courseId });
      setCourseVideoDeletion(response.data?.operation || null);
      setCourseVideoDeletionLoaded(true);
      if (shouldPollCourseVideoDeletion(response.data?.operation)) {
        await loadStructure(courseId, { background: true });
      }
    } catch (error) {
      setCourseVideoDeletionLoaded(false);
      console.error("load course video deletion status:", error);
    }
  };

  const runControlledMuxPreparation = async () => {
    if (!courseVideoDeletionLoaded || courseDeletionActive || !controlledMuxLessons.length ||
        controlledMuxLessons.length > CONTROLLED_MUX_BATCH_LIMIT) return;
    const accepted = window.confirm(
      `เตรียม Mux เฉพาะ ${controlledMuxLessons.length} บทเรียนที่เลือก?\n\n` +
      "ระบบจะไม่เริ่ม full-course bulk และจะไม่เปลี่ยนนโยบายผู้เรียน"
    );
    if (!accepted) return;
    setControlledMuxBusy(true); setErr("");
    try {
      const callable = httpsCallable(functions, "prepareLessonMuxBackup");
      for (const lesson of controlledMuxLessons) {
        await callable({ courseId, headerId: lesson.headerId,
          sectionId: lesson.sectionId, itemId: lesson.itemId });
      }
      setControlledMuxLessons([]);
      await loadStructure(courseId, { background: true });
      await loadMuxPreparationStatus();
    } catch (error) {
      setErr(error?.message || "เตรียมวิดีโอสำรองแบบเลือกบทเรียนไม่สำเร็จ");
    } finally { setControlledMuxBusy(false); }
  };

  useEffect(() => {
    if (!isEdit || !courseId) return undefined;
    loadCourseVideoDeletionStatus();
    return startCoursePreparationPolling({
      active: shouldPollCourseVideoDeletion(courseVideoDeletion),
      refresh: loadCourseVideoDeletionStatus,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, isEdit, courseVideoDeletion?.status]);

  const stageCourseVideoDelete = async () => {
    if (!isExactCourseVideoDeleteConfirmation(courseVideoDeletionConfirmation)) return;
    setCourseVideoDeletionBusy(true); setErr("");
    try {
      const callable = httpsCallable(functions, "stageCourseVideoDeletion");
      const response = await callable({ courseId, confirmation: courseVideoDeletionConfirmation });
      setCourseVideoDeletion(response.data?.operation || null);
      setStandbyAvailability("disabled");
      setShowCourseVideoDeletionModal(false); setCourseVideoDeletionConfirmation("");
      await loadCourseVideoDeletionStatus();
    } catch (error) {
      setErr(error?.message || "เริ่มลบวิดีโอทั้งหมดไม่สำเร็จ");
    } finally { setCourseVideoDeletionBusy(false); }
  };

  const startMuxPreparation = async () => {
    setMuxPreparationBusy(true);
    try {
      const callable = httpsCallable(functions, "prepareCourseMuxBackups");
      const preview = await callable({ courseId, confirm: false });
      const summary = preview.data?.summary || {};
      const accepted = window.confirm(
        `เตรียม Mux backup สำหรับ ${Number(summary.queued || 0)} รายการ?\n` +
        `พร้อมแล้ว ${Number(summary.matching || 0)} · ยังไม่มี ${Number(summary.missing || 0)} · ` +
        `ต้องอัปเดต ${Number(summary.stale || 0)} · ผิดพลาด ${Number(summary.error || 0)}\n` +
        `เวลาวิดีโอที่ประเมินได้ประมาณ ${Number(summary.estimatedMinutes || 0)} นาที\n\n` +
        "การเตรียม Mux backup ยังไม่เปิดให้ผู้เรียนใช้งาน"
      );
      if (!accepted) return;
      await callable({ courseId, confirm: true });
      await loadMuxPreparationStatus();
    } catch (error) {
      setErr(error?.message || "สร้าง Mux preparation job ไม่สำเร็จ");
    } finally {
      setMuxPreparationBusy(false);
    }
  };

  const controlMuxPreparation = async (action) => {
    setMuxPreparationBusy(true);
    try {
      const callable = httpsCallable(functions, "controlCourseMuxPreparationJob");
      await callable({ courseId, action });
      await loadMuxPreparationStatus();
      await loadStructure(courseId);
    } catch (error) {
      setErr(error?.message || "อัปเดต Mux preparation job ไม่สำเร็จ");
    } finally {
      setMuxPreparationBusy(false);
    }
  };

  const updateStandbyAvailability = async (nextAvailability) => {
    if (!isEdit || standbyPolicyBusy || nextAvailability === standbyAvailability) return;
    const previous = standbyAvailability;
    setStandbyAvailability(nextAvailability);
    setStandbyPolicyBusy(true);
    setErr("");
    try {
      const callable = httpsCallable(functions, "updateVideoStandbyPlaybackPolicy");
      const response = await callable({ courseId, availability: nextAvailability });
      setStandbyAvailability(response.data?.availability || nextAvailability);
    } catch (error) {
      setStandbyAvailability(previous);
      console.error("update standby playback policy failed", { code: error?.code || "unknown" });
      setErr(standbyPolicyUpdateErrorMessage());
    } finally {
      setStandbyPolicyBusy(false);
    }
  };

  const buildCoursePayload = () => {
    const createdBy = {
      uid: user?.uid || "",
      name: userDoc?.displayName || user?.displayName || "",
    };

    return {
      title: title.trim(),
      subTitle: subTitle.trim(),
      slug: slug.trim(),

      courseType: courseType || "thaibar",
      categories: safeArray(categories).filter(Boolean),
      level: level || "beginner",

      imageCoverUrl: imageCoverUrl.trim(),
      videoAdsUrl: videoAdsUrl.trim(),

      lineGroupQrImage: {
        imageUrl: String(lineGroupQrImage?.imageUrl || "").trim(),
        storagePath: String(lineGroupQrImage?.storagePath || "").trim(),
        fileName: String(lineGroupQrImage?.fileName || "").trim(),
        size: Number(lineGroupQrImage?.size || 0),
        uploadedAt: lineGroupQrImage?.uploadedAt || "",
        isActive: lineGroupQrImage?.isActive !== false,
      },

      courseHighlights: cleanLinesToArray(highlightsText),
      courseDescriptions: courseDescriptions.trim(),
      courseReceive: cleanLinesToArray(receiveText),

      courseDownloads: normalizeCourseDownloads(courseDownloads),

      price: courseType === "thaibar" ? 0 : Number(price || 0),
      priceText: courseType === "thaibar" ? "" : String(priceText || "").trim(),

      packagePrices:
        courseType === "thaibar"
          ? Object.fromEntries(
              THAIBAR_PACKAGE_OPTIONS.map((pkg) => [
                pkg.value,
                {
                  price: Number(packagePrices?.[pkg.value]?.price || 0),
                  priceText: String(packagePrices?.[pkg.value]?.priceText || "").trim(),
                },
              ])
            )
          : {},

      allowInstallment: !!allowInstallment,
      maxInstallments: !!allowInstallment
        ? Math.max(2, Number(maxInstallments || 2))
        : 0,

      installmentPackageKeys:
        !!allowInstallment && courseType === "thaibar"
          ? safeArray(installmentPackageKeys).filter((pkgValue) =>
              THAIBAR_PACKAGE_OPTIONS.some((pkg) => pkg.value === pkgValue)
            )
          : [],

      affiliateEnabled: !!affiliateEnabled,
      affiliateCommissionPercent: normalizeAffiliateCommissionPercent(
        affiliateCommissionPercent,
        DEFAULT_AFFILIATE_COURSE_SETTINGS.affiliateCommissionPercent
      ),

          showCourseCalendarImages: showCourseCalendarImages !== false,
          showCourseScheduleImages: showCourseScheduleImages !== false,
          showCourseChecklistImages: showCourseChecklistImages !== false,

          courseCalendarImages: safeArray(courseCalendarImages)
          .filter((x) => x?.name?.trim() || x?.imageUrl)
          .map((x) => ({
            id: x.id,
            name: String(x.name || "").trim(),
            imageUrl: String(x.imageUrl || "").trim(),
            storagePath: String(x.storagePath || "").trim(),
            fileName: String(x.fileName || "").trim(),
            size: Number(x.size || 0),
            uploadedAt: x.uploadedAt || "",
            isActive: x.isActive !== false,
          })),

        courseScheduleImages: safeArray(courseScheduleImages)
          .filter((x) => x?.name?.trim() || x?.imageUrl)
          .map((x) => ({
            id: x.id,
            name: String(x.name || "").trim(),
            imageUrl: String(x.imageUrl || "").trim(),
            storagePath: String(x.storagePath || "").trim(),
            fileName: String(x.fileName || "").trim(),
            size: Number(x.size || 0),
            uploadedAt: x.uploadedAt || "",
            isActive: x.isActive !== false,
          })),

        courseChecklistImages: safeArray(courseChecklistImages)
          .filter((x) => x?.name?.trim() || x?.imageUrl)
          .map((x) => ({
            id: x.id,
            name: String(x.name || "").trim(),
            imageUrl: String(x.imageUrl || "").trim(),
            storagePath: String(x.storagePath || "").trim(),
            fileName: String(x.fileName || "").trim(),
            size: Number(x.size || 0),
            uploadedAt: x.uploadedAt || "",
            isActive: x.isActive !== false,
          })),

      isActive: !!isActive,
      isFeatured: !!isFeatured,

      ownerUid: user?.uid || "",
      createdBy,

      updatedAt: serverTimestamp(),
    };
  };

  const onSaveCourse = async (e) => {
    e?.preventDefault?.();
    if (!canAccess) return;

    setErr("");

    if (!title.trim()) return setErr("กรุณากรอกชื่อคอร์ส");
    if (!slug.trim()) return setErr("กรุณากรอก slug");
    if (!categories?.length) return setErr("กรุณาเลือกหมวดหมู่อย่างน้อย 1 หมวด");

    if (courseType === "thaibar") {
      const hasAtLeastOnePrice = THAIBAR_PACKAGE_OPTIONS.some(
        (pkg) => Number(packagePrices?.[pkg.value]?.price || 0) > 0
      );
      if (!hasAtLeastOnePrice) {
        return setErr("กรุณากรอกราคาแพ็กเกจของคอร์สเนติอย่างน้อย 1 รายการ");
      }
    }

    if (allowInstallment) {
      const n = Number(maxInstallments || 0);
      if (!Number.isInteger(n) || n < 2) {
        return setErr("กรุณากรอกจำนวนงวดผ่อนชำระอย่างน้อย 2 งวด");
      }

      if (courseType === "thaibar" && safeArray(installmentPackageKeys).length === 0) {
        return setErr("กรุณาเลือกแพ็กเกจที่อนุญาตให้ผ่อนชำระอย่างน้อย 1 รายการ");
      }
    }

    if (affiliateEnabled) {
      const percent = Number(affiliateCommissionPercent || 0);
      if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
        return setErr("กรุณากรอกค่าคอมมิชชัน Affiliate ระหว่าง 0-100%");
      }
    }

    try {
      setSaving(true);

      if (!isEdit) {
        const payload = {
          ...buildCoursePayload(),
          createdAt: serverTimestamp(),
        };

        const refDoc = await addDoc(collection(db, "courses"), payload);
        setSaving(false);
        navigate(`/account/creator/courses/${refDoc.id}`, { replace: true });
        return;
      }

      await updateDoc(doc(db, "courses", courseId), buildCoursePayload());
      setSaving(false);
    } catch (e2) {
      console.error(e2);
      setErr(e2?.message || "บันทึกไม่สำเร็จ");
      setSaving(false);
    }
  };

  const onDeleteCourse = async () => {
    if (!isEdit) return;

    const ok = window.confirm("ยืนยันลบคอร์สนี้?");
    if (!ok) return;

    try {
      setSaving(true);
      await deleteDoc(doc(db, "courses", courseId));
      setSaving(false);
      navigate("/account/creator/courses", { replace: true });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "ลบไม่สำเร็จ");
      setSaving(false);
    }
  };

  const handleUploadCover = async (file) => {
    if (!file) return;

    try {
      setUploadingCover(true);

      const path = `courses/${courseId || "temp"}/cover_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      setImageCoverUrl(url);
      setUploadingCover(false);
    } catch (e) {
      console.error(e);
      setErr("อัปโหลดรูปไม่สำเร็จ");
      setUploadingCover(false);
    }
  };

  const getHeaderRef = (headerId) =>
    doc(db, "courses", courseId, "headers", headerId);

  const getSectionRef = (headerId, sectionId) =>
    doc(db, "courses", courseId, "headers", headerId, "sections", sectionId);

  const getItemRef = (headerId, sectionId, itemId) =>
    doc(
      db,
      "courses",
      courseId,
      "headers",
      headerId,
      "sections",
      sectionId,
      "items",
      itemId
    );

  const makeLessonKey = (headerId, sectionId, itemId) =>
    `${headerId}_${sectionId}_${itemId}`;

  const stopVideoItemListener = (lessonKey) => {
    const unsub = videoItemRealtimeRefs.current?.[lessonKey];
    if (typeof unsub === "function") {
      try {
        unsub();
      } catch {}
    }
    delete videoItemRealtimeRefs.current[lessonKey];
  };

  const updateLocalItem = (headerId, sectionId, itemId, updater) => {
    setHeaders((prev) =>
      prev.map((h) => {
        if (h.id !== headerId) return h;
        return {
          ...h,
          sections: safeArray(h.sections).map((s) => {
            if (s.id !== sectionId) return s;
            return {
              ...s,
              items: safeArray(s.items).map((it) => {
                if (it.id !== itemId) return it;
                return typeof updater === "function" ? updater(it) : { ...it, ...updater };
              }),
            };
          }),
        };
      })
    );
  };

  const updateLocalHeader = (headerId, patch) => {
    setHeaders((prev) =>
      prev.map((h) => (h.id === headerId ? { ...h, ...patch } : h))
    );
  };

  const updateLocalSection = (headerId, sectionId, patch) => {
    setHeaders((prev) =>
      prev.map((h) => {
        if (h.id !== headerId) return h;
        return {
          ...h,
          sections: safeArray(h.sections).map((s) =>
            s.id === sectionId ? { ...s, ...patch } : s
          ),
        };
      })
    );
  };

  useEffect(() => {
    if (!courseId) return;

    const wantedKeys = new Set();

    safeArray(headers).forEach((h) => {
      safeArray(h.sections).forEach((sec) => {
        safeArray(sec.items).forEach((it) => {
          const lessonKey = makeLessonKey(h.id, sec.id, it.id);
          const candidate = getCloudflareCandidateForEditor(it?.video || {});
          const current = getCloudflareCurrentForEditor(it?.video || {});
          const videoUid = candidate?.assetId || current?.assetId || "";
          const shouldObserve = shouldObserveCourseEditorVideo(it?.video || {});

          // Keep one authoritative item listener while either provider is still non-terminal.
          if (!videoUid || !shouldObserve) {
            stopVideoItemListener(lessonKey);
            return;
          }

          wantedKeys.add(lessonKey);

          // ถ้ามี listener อยู่แล้ว ไม่ต้อง subscribe ซ้ำ
          if (videoItemRealtimeRefs.current[lessonKey]) return;

          const unsub = onSnapshot(
            getItemRef(h.id, sec.id, it.id),
            (snap) => {
              if (!snap.exists()) {
                stopVideoItemListener(lessonKey);
                return;
              }

              const latest = snap.data();
              const latestVideo = latest?.video || {};
              const shouldContinueObserving = shouldObserveCourseEditorVideo(latestVideo);

              updateLocalItem(h.id, sec.id, it.id, {
                ...latest,
                id: it.id,
              });

              if (!shouldContinueObserving) {
                setVideoUploadingMap((prev) => {
                  const next = { ...prev };
                  delete next[lessonKey];
                  return next;
                });

                // All provider lifecycle states are terminal; release this lesson listener.
                stopVideoItemListener(lessonKey);
              }
            },
            (error) => {
              console.error("lesson realtime error:", error);
              stopVideoItemListener(lessonKey);
            }
          );

          videoItemRealtimeRefs.current[lessonKey] = unsub;
        });
      });
    });

    // cleanup listener ที่ไม่อยู่ในโครงสร้าง headers ปัจจุบันแล้ว
    Object.keys(videoItemRealtimeRefs.current).forEach((lessonKey) => {
      if (!wantedKeys.has(lessonKey)) {
        stopVideoItemListener(lessonKey);
      }
    });

    return () => {};
  }, [courseId, headers]);

  useEffect(() => {
    return () => {
      Object.keys(videoItemRealtimeRefs.current).forEach((lessonKey) => {
        const unsub = videoItemRealtimeRefs.current[lessonKey];
        if (typeof unsub === "function") {
          try {
            unsub();
          } catch {}
        }
      });
      videoItemRealtimeRefs.current = {};
    };
  }, []);

  useEffect(() => {
    if (!showHeaderModal) return;
    if (courseType !== "thaibar") return;

    const autoTitle = THAIBAR_HEADER_DEFAULT_TITLE_MAP[pendingHeaderType] || "";

    setPendingHeaderTitle((prev) => {
      const trimmedPrev = String(prev || "").trim();
      const isPrevAutoValue = Object.values(THAIBAR_HEADER_DEFAULT_TITLE_MAP).includes(trimmedPrev);

      // ถ้ายังไม่เคยพิมพ์เอง หรือค่าปัจจุบันยังเป็นค่า auto เดิมอยู่ ให้เปลี่ยนตาม type
      if (!headerTitleTouched || !trimmedPrev || isPrevAutoValue) {
        return autoTitle;
      }

      return prev;
    });
  }, [pendingHeaderType, showHeaderModal, courseType, headerTitleTouched]);

  const openAddHeaderModal = () => {
    setErr("");
    setPendingHeaderType("civil");
    setHeaderTitleTouched(false);

    if (courseType === "thaibar") {
      setPendingHeaderTitle(THAIBAR_HEADER_DEFAULT_TITLE_MAP.civil || "");
    } else {
      setPendingHeaderTitle("");
    }

    setShowHeaderModal(true);
  };

  const closeAddHeaderModal = () => {
    setShowHeaderModal(false);
    setPendingHeaderTitle("");
    setPendingHeaderType("civil");
    setHeaderTitleTouched(false);
  };

  const openAddSectionModal = (headerId) => {
    setErr("");
    setTargetHeaderIdForSection(headerId);
    setPendingSectionTitle("");
    setShowSectionModal(true);
  };

  const closeAddSectionModal = () => {
    setShowSectionModal(false);
    setTargetHeaderIdForSection("");
    setPendingSectionTitle("");
  };

  const openAddLessonModal = (headerId, sectionId) => {
    setErr("");
    setTargetHeaderIdForLesson(headerId);
    setTargetSectionIdForLesson(sectionId);
    setPendingLessonTitle("");
    setShowLessonModal(true);
  };

  const closeAddLessonModal = () => {
    setShowLessonModal(false);
    setPendingLessonTitle("");
    setTargetHeaderIdForLesson("");
    setTargetSectionIdForLesson("");
  };

  const addHeader = async () => {
    if (!isEdit) {
      setErr("กรุณาบันทึกคอร์สก่อน แล้วจึงเพิ่ม Header ได้");
      return;
    }

    openAddHeaderModal();
  };

  const submitAddHeader = async () => {
    if (!isEdit) {
      setErr("กรุณาบันทึกคอร์สก่อน แล้วจึงเพิ่ม Header ได้");
      return;
    }

    const headerTitle = String(pendingHeaderTitle || "").trim();
    if (!headerTitle) {
      setErr("กรุณากรอกชื่อ Header");
      return;
    }

    let headerType = "";
    if (courseType === "thaibar") {
      const allowed = new Set(THAIBAR_HEADER_TYPES.map((x) => x.value));
      if (!allowed.has(pendingHeaderType)) {
        setErr("ประเภท Header ไม่ถูกต้อง");
        return;
      }
      headerType = pendingHeaderType;
    }

    try {
      setErr("");
      const nextOrder = (headers?.length || 0) + 1;

      await addDoc(collection(db, "courses", courseId, "headers"), {
        title: headerTitle,
        headerType: headerType || "",
        order: nextOrder,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      closeAddHeaderModal();
      await loadStructure(courseId);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "เพิ่ม Header ไม่สำเร็จ");
    }
  };

  const updateHeaderTitle = async (headerId, value) => {
    try {
      updateLocalHeader(headerId, { title: value });
      await updateDoc(getHeaderRef(headerId), {
        title: value,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "แก้ไขชื่อ Header ไม่สำเร็จ");
      await loadStructure(courseId);
    }
  };

  const removeHeader = async (headerId) => {
    const ok = window.confirm("ยืนยันลบ Header นี้?");
    if (!ok) return;

    try {
      await deleteDoc(getHeaderRef(headerId));
      await loadStructure(courseId);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "ลบ Header ไม่สำเร็จ");
    }
  };

  const addSection = async (headerId) => {
    if (!isEdit) {
      setErr("กรุณาบันทึกคอร์สก่อน");
      return;
    }

    openAddSectionModal(headerId);
  };

  const submitAddSection = async () => {
    if (!isEdit) {
      setErr("กรุณาบันทึกคอร์สก่อน");
      return;
    }

    if (!targetHeaderIdForSection) {
      setErr("ไม่พบ Header เป้าหมาย");
      return;
    }

    const name = String(pendingSectionTitle || "").trim();
    if (!name) {
      setErr("กรุณากรอกชื่อ Section");
      return;
    }

    try {
      setErr("");
      const h = headers.find((x) => x.id === targetHeaderIdForSection);
      const nextOrder = (h?.sections?.length || 0) + 1;

      await addDoc(
        collection(db, "courses", courseId, "headers", targetHeaderIdForSection, "sections"),
        {
          title: name,
          order: nextOrder,
          isActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      );

      closeAddSectionModal();
      await loadStructure(courseId);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "เพิ่ม Section ไม่สำเร็จ");
    }
  };

  const updateSectionTitle = async (headerId, sectionId, value) => {
    try {
      updateLocalSection(headerId, sectionId, { title: value });
      await updateDoc(getSectionRef(headerId, sectionId), {
        title: value,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "แก้ไขชื่อ Section ไม่สำเร็จ");
      await loadStructure(courseId);
    }
  };

  const removeSection = async (headerId, sectionId) => {
    const ok = window.confirm("ยืนยันลบ Section นี้?");
    if (!ok) return;

    try {
      await deleteDoc(getSectionRef(headerId, sectionId));
      await loadStructure(courseId);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "ลบ Section ไม่สำเร็จ");
    }
  };

  const addItem = async (headerId, sectionId) => {
    if (!isEdit) {
      setErr("กรุณาบันทึกคอร์สก่อน");
      return;
    }

    openAddLessonModal(headerId, sectionId);
  };

  const submitAddLesson = async () => {
    if (!isEdit) {
      setErr("กรุณาบันทึกคอร์สก่อน");
      return;
    }

    if (!targetHeaderIdForLesson || !targetSectionIdForLesson) {
      setErr("ไม่พบตำแหน่ง Section ที่ต้องการเพิ่มหัวข้อการเรียน");
      return;
    }

    const name = String(pendingLessonTitle || "").trim();
    if (!name) {
      setErr("กรุณากรอกชื่อหัวข้อการเรียน");
      return;
    }

    try {
      setErr("");

      const h = headers.find((x) => x.id === targetHeaderIdForLesson);
      const sec = h?.sections?.find((s) => s.id === targetSectionIdForLesson);
      const nextOrder = (sec?.items?.length || 0) + 1;

      await addDoc(
        collection(
          db,
          "courses",
          courseId,
          "headers",
          targetHeaderIdForLesson,
          "sections",
          targetSectionIdForLesson,
          "items"
        ),
        {
          title: name,
          order: nextOrder,
          isActive: true,
          isPreview: false,

          releaseAt: null,
          releaseDateText: "",
          contentStatus: "draft",

          video: {
            schemaVersion: 1,
            assetRevision: 0,
            canonical: {
              durationSec: 0,
              sourceFileName: "",
            },
            providers: {
              cloudflare: { current: null, candidate: null },
              mux: { current: null, candidate: null },
            },
            provider: "cloudflare_stream",
            uid: "",
            status: "empty",
            fileName: "",
            fileSize: 0,
            mimeType: "",
            thumbnailUrl: "",
            durationSec: 0,
            isReady: false,
            readyToStream: false,
            requireSignedURLs: true,
          },

          documents: [],

          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      );

      closeAddLessonModal();
      await loadStructure(courseId);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "เพิ่มหัวข้อการเรียนไม่สำเร็จ");
    }
  };

  const updateItemTitle = async (headerId, sectionId, itemId, value) => {
    try {
      const next = value.trim();
      updateLocalItem(headerId, sectionId, itemId, { title: next });

      await updateDoc(getItemRef(headerId, sectionId, itemId), {
        title: next,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "แก้ไขชื่อหัวข้อไม่สำเร็จ");
      await loadStructure(courseId);
    }
  };

  const updateItemReleaseDate = async (headerId, sectionId, itemId, dateValue) => {
    try {
      const ts = fromDateInputValue(dateValue);

      const currentItem = headers
        .find((h) => h.id === headerId)
        ?.sections?.find((s) => s.id === sectionId)
        ?.items?.find((it) => it.id === itemId);

      const contentStatus = getLessonContentStatus({
        releaseAt: ts,
        video: currentItem?.video || {},
        manualPublished: false,
      });

      updateLocalItem(headerId, sectionId, itemId, {
        releaseAt: ts,
        releaseDateText: dateValue || "",
        contentStatus,
      });

      await updateDoc(getItemRef(headerId, sectionId, itemId), {
        releaseAt: ts,
        releaseDateText: dateValue || "",
        contentStatus,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "บันทึกวันที่ปล่อยไม่สำเร็จ");
      await loadStructure(courseId);
    }
  };

  const toggleItemFlag = async (headerId, sectionId, itemId, field, currentValue) => {
    try {
      updateLocalItem(headerId, sectionId, itemId, { [field]: !currentValue });

      await updateDoc(getItemRef(headerId, sectionId, itemId), {
        [field]: !currentValue,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "อัปเดตสถานะไม่สำเร็จ");
      await loadStructure(courseId);
    }
  };

  const removeItem = async (headerId, sectionId, itemId) => {
    const ok = window.confirm("ยืนยันลบหัวข้อนี้?");
    if (!ok) return;

    try {
      await deleteDoc(getItemRef(headerId, sectionId, itemId));
      await loadStructure(courseId);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "ลบหัวข้อไม่สำเร็จ");
    }
  };

  const updateItemVideoField = async (headerId, sectionId, itemId, key, value) => {
    try {
      const currentItem = headers
        .find((h) => h.id === headerId)
        ?.sections?.find((s) => s.id === sectionId)
        ?.items?.find((it) => it.id === itemId);

      const nextVideo = {
        ...(currentItem?.video || {}),
        [key]: value,
      };

      const contentStatus = getLessonContentStatus({
        releaseAt: currentItem?.releaseAt || null,
        video: nextVideo,
        manualPublished: false,
      });

      updateLocalItem(headerId, sectionId, itemId, {
        video: nextVideo,
        contentStatus,
      });

      await updateDoc(getItemRef(headerId, sectionId, itemId), {
        [`video.${key}`]: value,
        ...(key === "durationSec"
          ? { "video.canonical.durationSec": Number(value || 0) }
          : {}),
        contentStatus,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "อัปเดตวิดีโอไม่สำเร็จ");
      await loadStructure(courseId);
    }
  };

  const patchVideoProviderUpload = (lessonKey, provider, patch) => {
    setVideoUploadingMap((prev) => ({
      ...prev,
      [lessonKey]: {
        ...(prev[lessonKey] || {}),
        [provider]: { ...(prev[lessonKey]?.[provider] || {}), ...patch },
      },
    }));
  };

  const uploadFileToMux = ({
    lessonKey, file, uploadURL, operationId, generation,
    headerId, sectionId, itemId,
  }) => new Promise((resolve) => {
    const completeUpload = httpsCallable(functions, "markMuxUploadCompleted");
    const failUpload = httpsCallable(functions, "markMuxUploadFailed");
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadURL, true);
    xhr.setRequestHeader("Content-Type", file.type || "video/mp4");
    xhr.upload.onprogress = (event) => {
      const progress = event.lengthComputable ? Math.floor((event.loaded / event.total) * 100) : 0;
      patchVideoProviderUpload(lessonKey, "mux", {
        status: "uploading", progress, text: `กำลังอัปโหลด ${progress}%`,
      });
    };
    xhr.onload = async () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          await completeUpload({ courseId, headerId, sectionId, itemId, operationId, generation });
          patchVideoProviderUpload(lessonKey, "mux", {
            status: "processing", progress: 100, text: "อัปโหลดเสร็จแล้ว กำลังรอ Mux ประมวลผล...",
          });
          resolve(true);
        } catch (error) {
          patchVideoProviderUpload(lessonKey, "mux", {
            status: "processing", progress: 100,
            text: "อัปโหลดเสร็จแล้ว กำลังรอระบบยืนยันสถานะ",
          });
          console.error("Mux completion confirmation pending:", error);
          await loadStructure(courseId).catch((refreshError) =>
            console.error("refresh Mux authoritative state:", refreshError));
          resolve(true);
        }
        return;
      }
      const reason = `Mux upload failed (${xhr.status})`;
      patchVideoProviderUpload(lessonKey, "mux", { status: "error", text: "อัปโหลด Mux ไม่สำเร็จ" });
      failUpload({ courseId, headerId, sectionId, itemId, operationId, generation, reasonCode: reason })
        .catch((error) => console.error("mark Mux upload failed:", error));
      resolve(false);
    };
    xhr.onerror = () => {
      patchVideoProviderUpload(lessonKey, "mux", { status: "error", text: "เครือข่าย Mux ขัดข้อง" });
      failUpload({ courseId, headerId, sectionId, itemId, operationId, generation,
        reasonCode: "mux-upload-network" }).catch((error) => console.error(error));
      resolve(false);
    };
    xhr.send(file);
  });

  const startCloudflareUpload = ({
    lessonKey, file, uploadURL, uid, generation, operationId, headerId, sectionId, itemId,
  }) => {
    const completeUpload = httpsCallable(functions, "markStreamUploadCompleted");
    const failUpload = httpsCallable(functions, "markStreamUploadFailed");
    const upload = new tus.Upload(file, {
      uploadUrl: uploadURL,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      uploadSize: file.size,
      metadata: { filename: file.name, filetype: file.type || "video/mp4" },
      removeFingerprintOnSuccess: true,
      chunkSize: 50 * 1024 * 1024,
      onError: (error) => {
        patchVideoProviderUpload(lessonKey, "cloudflare", {
          status: "error", progress: 0, text: error?.message || "อัปโหลด Cloudflare ไม่สำเร็จ",
        });
        failUpload({ courseId, headerId, sectionId, itemId, videoUid: uid,
          generation, operationId, errorMessage: error?.message || "อัปโหลดไม่สำเร็จ" })
          .catch((failureError) => console.error("mark upload failed:", failureError));
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        const progress = bytesTotal > 0 ? Math.floor((bytesUploaded / bytesTotal) * 100) : 0;
        patchVideoProviderUpload(lessonKey, "cloudflare", {
          status: "uploading", progress, text: `กำลังอัปโหลด ${progress}%`,
        });
      },
      onSuccess: async () => {
        try {
          await completeUpload({ courseId, headerId, sectionId, itemId,
            videoUid: uid, generation, operationId });
          patchVideoProviderUpload(lessonKey, "cloudflare", {
            status: "processing", progress: 100,
            text: "อัปโหลดเสร็จแล้ว กำลังรอ Cloudflare ประมวลผล...",
          });
          await loadStructure(courseId);
        } catch (error) {
          patchVideoProviderUpload(lessonKey, "cloudflare", {
            status: "processing", progress: 100,
            text: "อัปโหลดเสร็จแล้ว กำลังรอระบบยืนยันสถานะ",
          });
          console.error("Cloudflare completion confirmation pending:", error);
          await loadStructure(courseId).catch((refreshError) =>
            console.error("refresh Cloudflare authoritative state:", refreshError));
        }
      },
    });
    upload.start();
  };

  const handleUploadVideoToCloudflare = async (headerId, sectionId, itemId, file) => {
    if (!file) return;

    const lessonKey = makeLessonKey(headerId, sectionId, itemId);

    const uploadMode = videoUploadModeMap[lessonKey] || "cloudflare_only";
    videoUploadFileRef.current[lessonKey] = file;
    try {
      setErr("");
      setVideoUploadingMap((prev) => ({
        ...prev,
        [lessonKey]: {
          uploadMode, operationId: "",
          cloudflare: { status: "preparing", progress: 0, text: "กำลังเตรียม Cloudflare..." },
          mux: uploadMode === "cloudflare_and_mux"
            ? { status: "preparing", progress: 0, text: "กำลังเตรียม Mux สำรอง..." }
            : { status: "not_requested", progress: 0, text: "ไม่ได้เลือก Mux สำรอง" },
        },
      }));
      const createUpload = httpsCallable(functions, "createStreamDirectUpload");
      const res = await createUpload({
        courseId,
        headerId,
        sectionId,
        itemId,
        uploadMode,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || "video/mp4",
      });
      const { operationId, providers = {} } = res.data || {};
      const cloudflare = providers.cloudflare || {};
      const mux = providers.mux || {};
      setVideoUploadingMap((prev) => ({
        ...prev,
        [lessonKey]: {
          ...(prev[lessonKey] || {}), operationId,
          cloudflare: cloudflare.ok
            ? { status: "uploading", progress: 0, text: "กำลังเริ่มอัปโหลด Cloudflare..." }
            : { status: "error", progress: 0, text: "เตรียม Cloudflare ไม่สำเร็จ" },
          mux: !mux.requested
            ? { status: "not_requested", progress: 0, text: "ไม่ได้เลือก Mux สำรอง" }
            : mux.ok
              ? { status: "uploading", progress: 0, text: "กำลังเริ่มอัปโหลด Mux สำรอง..." }
              : { status: "error", progress: 0, text: "เตรียม Mux สำรองไม่สำเร็จ" },
        },
      }));
      await loadStructure(courseId);
      // Start the primary first. Mux begins independently using the same File object.
      if (cloudflare.ok && cloudflare.uploadURL && cloudflare.assetId) {
        startCloudflareUpload({ lessonKey, file, uploadURL: cloudflare.uploadURL,
          uid: cloudflare.assetId, generation: cloudflare.generation, operationId,
          headerId, sectionId, itemId });
      }
      if (mux.requested && mux.ok && mux.uploadURL) {
        uploadFileToMux({ lessonKey, file, uploadURL: mux.uploadURL, operationId,
          generation: mux.generation, headerId, sectionId, itemId });
      }
    } catch (e) {
      console.error(e);
      setErr(e?.message || "อัปโหลดวิดีโอไม่สำเร็จ");

      setVideoUploadingMap((prev) => ({
        ...prev,
        [lessonKey]: {
          ...(prev[lessonKey] || {}),
          cloudflare: { status: "error", progress: 0, text: e?.message || "เตรียมอัปโหลดไม่สำเร็จ" },
        },
      }));
    }
  };

  const retryMuxUpload = async (headerId, sectionId, itemId) => {
    const lessonKey = makeLessonKey(headerId, sectionId, itemId);
    const file = videoUploadFileRef.current[lessonKey];
    const uploadInfo = videoUploadingMap[lessonKey];
    if (!file) {
      setErr("หลังรีโหลดหน้าไม่สามารถกู้ไฟล์เดิมได้ กรุณาเลือกไฟล์ใหม่ หรือเตรียม Mux จาก Cloudflare ภายหลัง");
      return;
    }
    try {
      patchVideoProviderUpload(lessonKey, "mux", { status: "preparing", text: "กำลังต่ออายุ Mux upload..." });
      const renew = httpsCallable(functions, "renewVideoUploadSession");
      const response = await renew({ courseId, headerId, sectionId, itemId,
        operationId: uploadInfo?.operationId, provider: "mux",
        fileName: file.name, fileSize: file.size, mimeType: file.type || "video/mp4" });
      const capability = response.data?.capability || {};
      await uploadFileToMux({ lessonKey, file, uploadURL: capability.uploadURL,
        operationId: uploadInfo.operationId, generation: response.data?.generation,
        headerId, sectionId, itemId });
    } catch (error) {
      patchVideoProviderUpload(lessonKey, "mux", { status: "error", text: "Retry Mux ไม่สำเร็จ" });
      setErr(error?.message || "Retry Mux ไม่สำเร็จ");
    }
  };

  const handleDeleteVideoFromCloudflare = async (
    headerId,
    sectionId,
    itemId,
    targetVideoUid = ""
  ) => {
    const lessonKey = makeLessonKey(headerId, sectionId, itemId);

    const currentItem = headers
      .find((h) => h.id === headerId)
      ?.sections?.find((s) => s.id === sectionId)
      ?.items?.find((it) => it.id === itemId);

    const currentAsset = getCloudflareCurrentForEditor(currentItem?.video || {});
    const candidateAsset = getCloudflareCandidateForEditor(currentItem?.video || {});
    const videoUid = targetVideoUid || currentAsset?.assetId || candidateAsset?.assetId || "";
    const deletingCandidate = !!videoUid && candidateAsset?.assetId === videoUid;

    const ok = window.confirm(
      deletingCandidate
        ? "ยืนยันยกเลิกและลบ replacement candidate นี้? วิดีโอ current จะไม่ถูกเปลี่ยนแปลง"
        : videoUid
        ? "ยืนยันลบวิดีโอนี้ออกจาก Cloudflare Stream และล้างข้อมูลวิดีโอในบทเรียน?"
        : "บทเรียนนี้ไม่มี Video UID แน่ชัด ต้องการล้างข้อมูลวิดีโอในบทเรียนหรือไม่?"
    );
    if (!ok) return;

    try {
      setErr("");

      setDeletingVideoMap((prev) => ({
        ...prev,
        [lessonKey]: true,
      }));

      const callable = httpsCallable(functions, "deleteStreamVideo");

      await callable({
        courseId,
        headerId,
        sectionId,
        itemId,
        videoUid,
      });

      setVideoUploadingMap((prev) => {
        const next = { ...prev };
        delete next[lessonKey];
        return next;
      });

      await loadStructure(courseId);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "ลบวิดีโอไม่สำเร็จ");
    } finally {
      setDeletingVideoMap((prev) => ({
        ...prev,
        [lessonKey]: false,
      }));
    }
  };

  const handleStageLessonVideoDeletion = async (headerId, sectionId, itemId, scope) => {
    const lessonKey = makeLessonKey(headerId, sectionId, itemId);
    const muxOnly = scope === "mux";
    const accepted = window.confirm(muxOnly
      ? "ยืนยันลบวิดีโอสำรอง Mux?\n\nระบบจะลบเฉพาะสำเนาวิดีโอสำรองบน Mux\nวิดีโอหลัก Cloudflare และบทเรียนจะยังใช้งานได้ตามปกติ\n\nหากต้องการใช้ระบบสำรองอีกครั้ง จะต้องเตรียมวิดีโอ Mux ใหม่"
      : "ยืนยันลบวิดีโอทั้งหมดออกจากบทเรียน?\n\nการดำเนินการนี้จะลบทั้งวิดีโอหลัก Cloudflare และวิดีโอสำรอง Mux\nตัวบทเรียนและข้อมูลการเรียนอื่นจะยังคงอยู่");
    if (!accepted) return;
    setDeletingVideoMap((prev) => ({ ...prev, [lessonKey]: true })); setErr("");
    try {
      const callable = httpsCallable(functions,
        muxOnly ? "stageLessonMuxDeletion" : "stageLessonVideoDeletion");
      await callable({ courseId, headerId, sectionId, itemId });
      await loadStructure(courseId, { background: true });
    } catch (error) {
      setErr(error?.message || "ลบวิดีโอไม่สำเร็จ");
    } finally {
      setDeletingVideoMap((prev) => ({ ...prev, [lessonKey]: false }));
    }
  };

  const handleUploadDocument = async (headerId, sectionId, itemId, file) => {
    if (!file) return;

    const uploadKey = `${headerId}_${sectionId}_${itemId}`;
    setUploadingDocKey(uploadKey);

    try {
      const path = `courses/${courseId}/documents/${itemId}/${file.name}`;
      const storageRef = ref(storage, path);
      const metadata = {
        contentType: file.type || "application/pdf",
        contentDisposition: `inline; filename*=UTF-8''${encodeURIComponent(file.name)}`,
      };

      await uploadBytes(storageRef, file, metadata);
      const url = await getDownloadURL(storageRef);

      const currentItem = headers
        .find((h) => h.id === headerId)
        ?.sections?.find((s) => s.id === sectionId)
        ?.items?.find((it) => it.id === itemId);

      const docs = safeArray(currentItem?.documents);

      const nextDocs = [
        ...docs,
        {
          name: file.name,
          fileUrl: url,
          storagePath: path,
          size: file.size || 0,
          ext: file.name.split(".").pop()?.toLowerCase() || "",
          uploadedAt: new Date().toISOString(),
        },
      ];

      updateLocalItem(headerId, sectionId, itemId, {
        documents: nextDocs,
      });

      await updateDoc(getItemRef(headerId, sectionId, itemId), {
        documents: nextDocs,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "อัปโหลดเอกสารไม่สำเร็จ");
      await loadStructure(courseId);
    } finally {
      setUploadingDocKey("");
    }
  };

  const removeDocumentFromItem = async (headerId, sectionId, itemId, docIndex) => {
    const ok = window.confirm("ยืนยันลบเอกสารนี้?");
    if (!ok) return;

    try {
      const currentItem = headers
        .find((h) => h.id === headerId)
        ?.sections?.find((s) => s.id === sectionId)
        ?.items?.find((it) => it.id === itemId);

      const docs = safeArray(currentItem?.documents);
      const targetDoc = docs[docIndex];
      const nextDocs = docs.filter((_, i) => i !== docIndex);

      updateLocalItem(headerId, sectionId, itemId, {
        documents: nextDocs,
      });

      await updateDoc(getItemRef(headerId, sectionId, itemId), {
        documents: nextDocs,
        updatedAt: serverTimestamp(),
      });

      if (targetDoc?.storagePath) {
        try {
          await deleteObject(ref(storage, targetDoc.storagePath));
        } catch (e) {
          console.warn("deleteObject document warning:", e?.message);
        }
      }
    } catch (e) {
      console.error(e);
      setErr(e?.message || "ลบเอกสารไม่สำเร็จ");
      await loadStructure(courseId);
    }
  };

  const loadCourseDownloadsOnly = async () => {
    if (!courseId) return;

    try {
      const snap = await getDoc(doc(db, "courses", courseId));
      if (!snap.exists()) return;

      const data = snap.data();
      setCourseDownloads(
        Array.isArray(data?.courseDownloads) && data.courseDownloads.length
          ? data.courseDownloads
          : [makeEmptyCourseDownload()]
      );
    } catch (e) {
      console.error("loadCourseDownloadsOnly error:", e);
    }
  };

  const addCourseDownloadRow = async () => {
    try {
      const next = [...safeArray(courseDownloads), makeEmptyCourseDownload()];
      setCourseDownloads(next);
      await saveCourseDownloadsToFirestore(next);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "เพิ่มเอกสารคอร์สไม่สำเร็จ");
      await loadCourseDownloadsOnly();
    }
  };

  const updateCourseDownloadRowLocal = (rowId, patch) => {
    setCourseDownloads((prev) =>
      safeArray(prev).map((x) => (x.id === rowId ? { ...x, ...patch } : x))
    );
  };

  const updateCourseDownloadRow = async (rowId, patch) => {
    try {
      const next = safeArray(courseDownloads).map((x) =>
        x.id === rowId ? { ...x, ...patch } : x
      );

      setCourseDownloads(next);
      await saveCourseDownloadsToFirestore(next);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "บันทึกเอกสารคอร์สไม่สำเร็จ");
      await loadCourseDownloadsOnly();
    }
  };

  const removeCourseDownloadRow = async (rowId) => {
    const row = safeArray(courseDownloads).find((x) => x.id === rowId);
    const ok = window.confirm("ยืนยันลบเอกสารนี้?");
    if (!ok) return;

    try {
      if (row?.storagePath) {
        try {
          await deleteObject(ref(storage, row.storagePath));
        } catch (e) {
          console.warn("delete course download warning:", e?.message);
        }
      }

      const next = safeArray(courseDownloads).filter((x) => x.id !== rowId);
      const nextSafe = next.length ? next : [makeEmptyCourseDownload()];

      setCourseDownloads(nextSafe);
      await saveCourseDownloadsToFirestore(nextSafe);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "ลบเอกสารคอร์สไม่สำเร็จ");
      await loadCourseDownloadsOnly();
    }
  };

  const handleUploadCourseDownload = async (rowId, file) => {
    if (!file || !courseId) return;

    try {
      setUploadingCourseDocId(rowId);

      const path = `courses/${courseId}/course-downloads/${file.name}`;
      const storageRef = ref(storage, path);
      const metadata = {
        contentType: file.type || "application/pdf",
        contentDisposition: `inline; filename*=UTF-8''${encodeURIComponent(file.name)}`,
      };

      await uploadBytes(storageRef, file, metadata);
      const url = await getDownloadURL(storageRef);

      const next = safeArray(courseDownloads).map((x) =>
        x.id === rowId
          ? {
              ...x,
              name: x.name?.trim() || file.name.replace(/\.[^/.]+$/, ""),
              fileUrl: url,
              storagePath: path,
              fileName: file.name,
              ext: file.name.split(".").pop()?.toLowerCase() || "",
              size: file.size || 0,
              uploadedAt: new Date().toISOString(),
            }
          : x
      );

      setCourseDownloads(next);
      await saveCourseDownloadsToFirestore(next);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "อัปโหลดเอกสารคอร์สไม่สำเร็จ");
      await loadCourseDownloadsOnly();
    } finally {
      setUploadingCourseDocId("");
    }
  };

  if (loading) return null;
  if (!user) return null;
  if (!canAccess) {
    return <Alert variant="warning">หน้านี้สำหรับ Creator/Admin เท่านั้น</Alert>;
  }

  if (loadingPage) {
    return (
      <div className="py-5 text-center text-muted">
        <Spinner animation="border" size="sm" /> กำลังโหลดข้อมูล...
      </div>
    );
  }

  const saveCourseDownloadsToFirestore = async (nextCourseDownloads) => {
    if (!courseId) return;

    const normalized = normalizeCourseDownloads(nextCourseDownloads);

    await updateDoc(doc(db, "courses", courseId), {
      courseDownloads: normalized,
      updatedAt: serverTimestamp(),
    });
  };

  const handleUploadSpecialCourseDownload = async (config, file) => {
    if (!file) return;
    if (!courseId) {
      setErr("กรุณาบันทึกคอร์สก่อน แล้วจึงอัปโหลดเอกสารได้");
      return;
    }

    const isPdf =
      String(file.name || "").toLowerCase().endsWith(".pdf") ||
      String(file.type || "").toLowerCase() === "application/pdf";
    if (!isPdf) {
      setErr("กรุณาเลือกไฟล์ PDF เท่านั้น");
      return;
    }

    const oldItems = safeArray(courseDownloads).filter(
      (item) => item?.scope === config.scope
    );
    const timestamp = Date.now();
    const path = `courses/${courseId}/course-downloads/${config.folder}/${timestamp}_${file.name}`;
    const storageRef = ref(storage, path);
    const metadata = {
      contentType: file.type || "application/pdf",
      contentDisposition: `inline; filename*=UTF-8''${encodeURIComponent(file.name)}`,
    };

    try {
      setErr("");
      setUploadingCourseDocId(config.scope);
      await uploadBytes(storageRef, file, metadata);
      const url = await getDownloadURL(storageRef);
      const newItem = {
        id: `statute_${timestamp}_${Math.random().toString(36).slice(2, 8)}`,
        scope: config.scope,
        name: config.heading,
        fileUrl: url,
        storagePath: path,
        fileName: file.name,
        ext: "pdf",
        size: file.size || 0,
        uploadedAt: new Date(timestamp).toISOString(),
        isActive: true,
      };
      const next = [
        ...safeArray(courseDownloads).filter((item) => item?.scope !== config.scope),
        newItem,
      ];

      await saveCourseDownloadsToFirestore(next);
      setCourseDownloads(next);

      await Promise.all(
        oldItems
          .filter((item) => item?.storagePath && item.storagePath !== path)
          .map(async (item) => {
            try {
              await deleteObject(ref(storage, item.storagePath));
            } catch (error) {
              console.warn("delete replaced special course download warning:", error?.message);
            }
          })
      );
    } catch (error) {
      console.error(error);
      setErr(error?.message || "อัปโหลดเอกสารมาตราสำคัญไม่สำเร็จ");
      await loadCourseDownloadsOnly();
    } finally {
      setUploadingCourseDocId("");
    }
  };

  const removeSpecialCourseDownload = async (config) => {
    const removedItems = safeArray(courseDownloads).filter(
      (item) => item?.scope === config.scope
    );
    if (!removedItems.length) return;
    if (!window.confirm(`ยืนยันลบไฟล์ ${config.heading}?`)) return;

    try {
      setErr("");
      const next = safeArray(courseDownloads).filter(
        (item) => item?.scope !== config.scope
      );
      await saveCourseDownloadsToFirestore(next);
      setCourseDownloads(next);

      await Promise.all(
        removedItems
          .filter((item) => item?.storagePath)
          .map(async (item) => {
            try {
              await deleteObject(ref(storage, item.storagePath));
            } catch (error) {
              console.warn("delete special course download warning:", error?.message);
            }
          })
      );
    } catch (error) {
      console.error(error);
      setErr(error?.message || "ลบเอกสารมาตราสำคัญไม่สำเร็จ");
      await loadCourseDownloadsOnly();
    }
  };

  const getCourseImageConfig = (type) => {
    if (type === "calendar") {
      return {
        list: courseCalendarImages,
        setter: setCourseCalendarImages,
        folder: "calendar-images",
      };
    }

    if (type === "schedule") {
      return {
        list: courseScheduleImages,
        setter: setCourseScheduleImages,
        folder: "schedule-images",
      };
    }

    return {
      list: courseChecklistImages,
      setter: setCourseChecklistImages,
      folder: "checklist-images",
    };
  };

  const updateCourseImageRowLocal = (type, rowId, patch) => {
    const { setter } = getCourseImageConfig(type);

    setter((prev) =>
      safeArray(prev).map((x) => (x.id === rowId ? { ...x, ...patch } : x))
    );
  };

  const addCourseImageRow = (type) => {
    const { setter } = getCourseImageConfig(type);

    setter((prev) => [...safeArray(prev), makeEmptyCourseImage()]);
  };


  const removeCourseImageRow = async (type, rowId) => {
    const { list, setter } = getCourseImageConfig(type);
    const row = safeArray(list).find((x) => x.id === rowId);

    const ok = window.confirm("ยืนยันลบรูปภาพนี้?");
    if (!ok) return;

    try {
      if (row?.storagePath) {
        try {
          await deleteObject(ref(storage, row.storagePath));
        } catch (e) {
          console.warn("delete course image warning:", e?.message);
        }
      }

      const next = safeArray(list).filter((x) => x.id !== rowId);
      setter(next.length ? next : [makeEmptyCourseImage()]);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "ลบรูปภาพไม่สำเร็จ");
    }
  };

  const handleUploadCourseImage = async (type, rowId, file) => {
    if (!file || !courseId) return;

    try {
      const key = `${type}_${rowId}`;
      setUploadingCourseImageKey(key);

      const { setter, folder } = getCourseImageConfig(type);
      const path = `courses/${courseId}/${folder}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);


      setter((prev) =>
        safeArray(prev).map((x) =>
          x.id === rowId
            ? {
                ...x,
                name: x.name?.trim() || file.name.replace(/\.[^/.]+$/, ""),
                imageUrl: url,
                storagePath: path,
                fileName: file.name,
                size: file.size || 0,
                uploadedAt: new Date().toISOString(),
              }
            : x
        )
      );
    } catch (e) {
      console.error(e);
      setErr(e?.message || "อัปโหลดรูปภาพไม่สำเร็จ");
    } finally {
      setUploadingCourseImageKey("");
    }
  };

  const handleUploadLineGroupQr = async (file) => {
    if (!file || !courseId) return;

    try {
      setUploadingLineGroupQr(true);

      const path = `courses/${courseId}/line-group-qr/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      setLineGroupQrImage({
        imageUrl: url,
        storagePath: path,
        fileName: file.name,
        size: file.size || 0,
        uploadedAt: new Date().toISOString(),
        isActive: true,
      });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "อัปโหลด QR กลุ่มติวไม่สำเร็จ");
    } finally {
      setUploadingLineGroupQr(false);
    }
  };

  const removeLineGroupQr = async () => {
    const ok = window.confirm("ยืนยันลบ QR กลุ่มติวนี้?");
    if (!ok) return;

    try {
      if (lineGroupQrImage?.storagePath) {
        try {
          await deleteObject(ref(storage, lineGroupQrImage.storagePath));
        } catch (e) {
          console.warn("delete line group qr warning:", e?.message);
        }
      }

      setLineGroupQrImage({
        imageUrl: "",
        storagePath: "",
        fileName: "",
        size: 0,
        uploadedAt: "",
        isActive: true,
      });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "ลบ QR กลุ่มติวไม่สำเร็จ");
    }
  };

  const openCurriculumPreview = () => {
    setShowCurriculumModal(true);

    if (safeArray(headers).length > 0) {
      setOpenHeaderKey("0");
      const firstHeader = headers[0];
      if (safeArray(firstHeader?.sections).length > 0) {
        setOpenSectionKey("0-0");
      } else {
        setOpenSectionKey("");
      }
    } else {
      setOpenHeaderKey("");
      setOpenSectionKey("");
    }
  };

  const closeCurriculumPreview = () => {
    setShowCurriculumModal(false);
  };

  const openHeaderAndSection = (hi, si = 0) => {
    setOpenHeaderKey(String(hi));
    setOpenSectionKey(`${hi}-${si}`);
  };

  

  const getLessonBulletStyle = (status) => {
    switch (status) {
      case "published":
        return {
          background: "#198754", // เขียว
          boxShadow: "0 0 0 3px rgba(25,135,84,.12)",
        };
      case "scheduled":
        return {
          background: "#fd7e14", // ส้ม
          boxShadow: "0 0 0 3px rgba(253,126,20,.12)",
        };
      default:
        return {
          background: "#adb5bd", // เทา
          boxShadow: "0 0 0 3px rgba(173,181,189,.16)",
        };
    }
  };

  
  const THAIBAR_PACKAGE_GROUPS = [
  {
    key: "guarantee",
    title: "แพ็กเกจการันตี (ยาว+สรุป)",
    items: ["F_2", "F_1_civil", "F_1_criminal"],
  },
  {
    key: "perfect",
    title: "แพ็กเกจเพอร์เฟค (ยาว+สรุป+ประมวล)",
    items: ["P_2", "P_1_civil", "P_1_criminal"],
  },
  {
    key: "long",
    title: "แพ็กเกจเนติบัณฑิต (ยาว)",
    items: ["L_2", "L_1_civil", "L_1_criminal"],
  },
  {
    key: "summary",
    title: "แพ็กเกจเนติบัณฑิต (สรุป)",
    items: ["S_2", "S_1_civil", "S_1_criminal"],
  },
];

const getThaibarPackageLabel = (pkgValue) =>
  THAIBAR_PACKAGE_OPTIONS.find((x) => x.value === pkgValue)?.label || pkgValue;

  const toggleEditorHeader = (headerId) => {
  setExpandedHeaders((prev) => ({
    ...prev,
    [headerId]: !prev[headerId],
  }));
};

const toggleEditorSection = (sectionId) => {
  setExpandedSections((prev) => ({
    ...prev,
    [sectionId]: !prev[sectionId],
  }));
};

const expandAllEditorStructure = () => {
  const nextHeaders = {};
  const nextSections = {};

  safeArray(headers).forEach((h) => {
    nextHeaders[h.id] = true;
    safeArray(h.sections).forEach((sec) => {
      nextSections[sec.id] = true;
    });
  });

  setExpandedHeaders(nextHeaders);
  setExpandedSections(nextSections);
};

const collapseAllEditorStructure = () => {
  setExpandedHeaders({});
  setExpandedSections({});
};

const isAllEditorExpanded =
  safeArray(headers).length > 0 &&
  safeArray(headers).every(
    (h) =>
      expandedHeaders[h.id] &&
      safeArray(h.sections).every((sec) => expandedSections[sec.id])
  );

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <h4 className="mb-0">{isEdit ? "แก้ไขคอร์ส" : "สร้างคอร์สใหม่"}</h4>

          {isEdit ? (
            <Button
              type="button"
              size="sm"
              variant="outline-dark"
              onClick={openCurriculumPreview}
            >
              ดูโครงสร้างคอร์ส
            </Button>
          ) : null}
        </div>

        <div className="d-flex gap-2">
          {isEdit && (
            <Button
              variant="outline-primary"
              onClick={() => navigate(`/account/creator/courses/${courseId}/preview?preview=1`)}
              disabled={saving}
            >
              ตัวอย่างหน้าติว
            </Button>
          )}
          {isEdit && (
            <Button variant="outline-danger" onClick={onDeleteCourse} disabled={saving}>
              ลบ
            </Button>
          )}
          <Button variant="outline-secondary" onClick={() => navigate(-1)} disabled={saving}>
            กลับ
          </Button>
          <Button variant="primary" onClick={onSaveCourse} disabled={saving}>
            {saving ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </div>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      {isEdit ? (
        <Card className="border mb-3">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
              <div>
                <div className="fw-semibold">Mux Warm Standby</div>
                <div className="small text-muted mt-1">
                  การเตรียม Mux backup ยังไม่เปิดให้ผู้เรียนใช้งาน
                </div>
              </div>
              <Badge bg={muxPreparation?.job?.status === "completed" ? "success"
                : muxPreparation?.job?.status === "running" ? "primary"
                  : muxPreparation?.job?.status === "error" ? "danger" : "secondary"}>
                {muxPreparation?.job?.status || "ยังไม่มี job"}
              </Badge>
            </div>
            <div className="mt-3" style={{ maxWidth: 360 }}>
              <Form.Label className="fw-semibold mb-1">ระบบ Streaming สำรอง</Form.Label>
              <Form.Select
                value={standbyAvailability}
                disabled={standbyPolicyBusy}
                onChange={(event) => updateStandbyAvailability(event.target.value)}
                aria-label="ระบบ Streaming สำรอง"
              >
                <option value="disabled">ปิด</option>
                <option value="admin_preview_only">เปิดสำหรับตัวอย่างหน้าติว</option>
                <option value="learner_enabled">เปิดให้ผู้เรียนใช้งาน</option>
              </Form.Select>
              <div className="small text-muted mt-1">
                ใช้สำหรับผู้ดูแลหรือเจ้าของคอร์สในหน้าตัวอย่างเท่านั้น
                {standbyPolicyBusy ? " · กำลังบันทึก..." : ""}
              </div>
            </div>
            <div className="mt-3">
              <span className="fw-semibold">
                {Number(muxPreparation?.summary?.matching || 0)} / {Number(muxPreparation?.summary?.eligible || 0)}
              </span>{" "}
              พร้อมและเป็นไฟล์ปัจจุบัน
            </div>
            <div className="small text-muted mt-1">
              {Number(muxPreparation?.summary?.processing || 0)} กำลังเตรียม ·{" "}
              {Number(muxPreparation?.summary?.missing || 0)} ยังไม่มี ·{" "}
              {Number(muxPreparation?.summary?.stale || 0)} ต้องอัปเดต ·{" "}
              {Number(muxPreparation?.summary?.error || 0)} ผิดพลาด
            </div>
            {muxPreparation?.job ? (
              <div className="small mt-2">
                ประมวลผล {Number(muxPreparation.job.completed || 0)} / {Number(muxPreparation.job.eligible || 0)} รายการที่เข้าเกณฑ์ ·{" "}
                ข้าม {Number(muxPreparation.job.skipped || 0)} รายการ ·{" "}
                กำลังดำเนินการ {Number(muxPreparation.job.processing || 0)} ·{" "}
                ผิดพลาด {Number(muxPreparation.job.error || 0)}
              </div>
            ) : null}
            <div className="d-flex gap-2 flex-wrap mt-3">
              <Button type="button" size="sm" variant="outline-primary"
                disabled={muxPreparationBusy || courseDeletionActive} onClick={startMuxPreparation}>
                เตรียม / อัปเดตวิดีโอสำรอง
              </Button>
              <Button type="button" size="sm" variant="outline-danger"
                disabled={muxPreparationBusy || !muxPreparation?.job?.error}
                onClick={() => controlMuxPreparation("retry_failed")}>
                ลองรายการที่ผิดพลาดอีกครั้ง
              </Button>
              {muxPreparation?.job?.status === "running" ? (
                <Button type="button" size="sm" variant="outline-secondary"
                  disabled={muxPreparationBusy} onClick={() => controlMuxPreparation("pause")}>
                  หยุดชั่วคราว
                </Button>
              ) : null}
              {muxPreparation?.job?.status === "paused" ? (
                <Button type="button" size="sm" variant="outline-success"
                  disabled={muxPreparationBusy} onClick={() => controlMuxPreparation("resume")}>
                  ทำงานต่อ
                </Button>
              ) : null}
              {["running", "paused"].includes(muxPreparation?.job?.status) ? (
                <Button type="button" size="sm" variant="outline-dark"
                  disabled={muxPreparationBusy} onClick={() => controlMuxPreparation("cancel")}>
                  ยกเลิก job
                </Button>
              ) : null}
            </div>
            <div className="border-top mt-3 pt-3">
              <div className="fw-semibold">Controlled Mux batch</div>
              <div className="small text-muted mt-1">
                เลือกเฉพาะบทเรียนสถานะ MISSING ด้านล่าง สูงสุด {CONTROLLED_MUX_BATCH_LIMIT} บทเรียน
              </div>
              <div className="small mt-2">เลือกแล้ว {controlledMuxLessons.length} บทเรียน</div>
              <Button type="button" size="sm" variant="outline-primary" className="mt-2"
                disabled={!courseVideoDeletionLoaded || courseDeletionActive || controlledMuxBusy ||
                  controlledMuxLessons.length === 0}
                onClick={runControlledMuxPreparation}>
                {controlledMuxBusy ? "กำลังเริ่มรายการที่เลือก..." : "เตรียม Mux เฉพาะบทเรียนที่เลือก"}
              </Button>
              {!courseVideoDeletionLoaded ? (
                <div className="small text-danger mt-1">ยังยืนยันสถานะ course deletion ไม่สำเร็จ จึงยังเริ่มไม่ได้</div>
              ) : courseDeletionActive ? (
                <div className="small text-danger mt-1">คอร์สกำลังลบวิดีโอทั้งหมด จึงยังเริ่มไม่ได้</div>
              ) : null}
            </div>
          </Card.Body>
        </Card>
      ) : null}

      {isEdit && VIDEO_DELETION_CONTROLS_VISIBLE ? (
        <Card className="border-danger mb-3">
          <Card.Body>
            <div className="fw-semibold text-danger">การจัดการวิดีโอ</div>
            <div className="small text-muted mt-1">
              ลบเฉพาะไฟล์วิดีโอ Cloudflare และ Mux ทั้งคอร์ส โดยคงโครงสร้าง บทเรียน เอกสาร การลงทะเบียน และความคืบหน้าไว้
            </div>
            {courseVideoDeletion ? (
              <div className="small mt-2">
                สถานะ: {courseVideoDeletion.status} · ลบออกจากบทเรียนแล้ว {courseVideoDeletion.detached} / {courseVideoDeletion.totalLessons}
                {courseVideoDeletion.cleanupFailed ? ` · ต้องตรวจสอบ ${courseVideoDeletion.cleanupFailed}` : ""}
              </div>
            ) : null}
            <Button type="button" variant="danger" className="mt-3"
              disabled={courseDeletionActive || courseVideoDeletionBusy}
              onClick={() => { setCourseVideoDeletionConfirmation(""); setShowCourseVideoDeletionModal(true); }}>
              {courseDeletionActive ? "กำลังลบวิดีโอทั้งหมด..." : "ลบวิดีโอทั้งหมด"}
            </Button>
          </Card.Body>
        </Card>
      ) : null}

      <Row className="g-3">
        <Col lg={7}>
          <Card className="border">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-2 gap-2 flex-wrap">
                <div className="fw-semibold">โครงสร้างคอร์ส (Headers / Sections / Lessons)</div>

                <div className="d-flex gap-2">
                  {headers.length > 0 ? (
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={isAllEditorExpanded ? collapseAllEditorStructure : expandAllEditorStructure}
                    >
                      {isAllEditorExpanded ? "ย่อทั้งหมด" : "แสดงทั้งหมด"}
                    </Button>
                  ) : null}

                  <Button variant="outline-primary" size="sm" onClick={addHeader}>
                    + Header
                  </Button>
                </div>
              </div>

              {!isEdit && (
                <Alert variant="info" className="mb-3">
                  บันทึกคอร์สก่อน แล้วค่อยเพิ่ม Header / Section / หัวข้อการเรียนได้
                </Alert>
              )}

              {shouldShowCourseStructurePlaceholder({
                loading: loadingStructure,
                hasStructure: headers.length > 0,
              }) ? (
                <div className="py-4 text-center text-muted">
                  <Spinner animation="border" size="sm" /> กำลังโหลด…
                </div>
              ) : headers.length === 0 ? (
                <div className="text-muted">ยังไม่มี Header</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {headers.map((h) => {
                    const headerExpanded = !!expandedHeaders[h.id];

                    return (
                      <Card key={h.id} className="border">
                      <Card.Body>
                        <div className="d-flex align-items-start justify-content-between gap-3">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                              <Badge bg="dark">Header {h.order}</Badge>
                              {h.headerType ? (
                                <Badge
                                  bg="light"
                                  text="dark"
                                  style={{ border: "1px solid rgba(0,0,0,.12)" }}
                                >
                                  {h.headerType}
                                </Badge>
                              ) : null}
                            </div>

                            <Form.Control
                              defaultValue={h.title || ""}
                              onBlur={(e) => {
                                const next = e.target.value.trim();
                                if (!next || next === h.title) return;
                                updateHeaderTitle(h.id, next);
                              }}
                              placeholder="ชื่อ Header"
                            />
                          </div>

                          <div className="d-flex gap-2">
                            <Button
                              type="button"
                              variant="outline-dark"
                              size="sm"
                              onClick={() => toggleEditorHeader(h.id)}
                            >
                              {headerExpanded ? "ซ่อน" : "เปิด"}
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => addSection(h.id)}
                              disabled={!isEdit}
                            >
                              + Section
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeHeader(h.id)}
                              disabled={!isEdit}
                            >
                              ลบ
                            </Button>
                          </div>
                        </div>

                        {headerExpanded ? (
                          <div className="mt-3 d-flex flex-column gap-3">
                            {safeArray(h.sections).length === 0 ? (
                            <div className="text-muted small">ยังไม่มี Section</div>
                          ) : (
                            safeArray(h.sections).map((sec) => {
                              const sectionExpanded = !!expandedSections[sec.id];

                              return (
                                <Card key={sec.id} className="border">
                                <Card.Body>
                                  <div className="d-flex align-items-start justify-content-between gap-3">
                                    <div className="flex-grow-1">
                                      <div className="mb-2">
                                        <Badge bg="secondary">Section {sec.order}</Badge>
                                      </div>
                                      <Form.Control
                                        defaultValue={sec.title || ""}
                                        onBlur={(e) => {
                                          const next = e.target.value.trim();
                                          if (!next || next === sec.title) return;
                                          updateSectionTitle(h.id, sec.id, next);
                                        }}
                                        placeholder="ชื่อ Section"
                                      />
                                    </div>

                                    <div className="d-flex gap-2">
                                      <Button
                                        type="button"
                                        variant="outline-dark"
                                        size="sm"
                                        onClick={() => toggleEditorSection(sec.id)}
                                      >
                                        {sectionExpanded ? "ซ่อน" : "เปิด"}
                                      </Button>
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => addItem(h.id, sec.id)}
                                        disabled={!isEdit}
                                      >
                                        + Lesson
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => removeSection(h.id, sec.id)}
                                        disabled={!isEdit}
                                      >
                                        ลบ
                                      </Button>
                                    </div>
                                  </div>

                                  {sectionExpanded ? (
                                    <div className="mt-3 d-flex flex-column gap-3">
                                      {safeArray(sec.items).length === 0 ? (
                                      <div className="text-muted small">ยังไม่มีหัวข้อการเรียน</div>
                                    ) : (
                                      safeArray(sec.items).map((it) => {
                                        const releaseInputValue = toDateInputValue(
                                          it?.releaseAt || it?.releaseDateText
                                        );
                                        const docs = safeArray(it?.documents);
                                        const videoObj = it?.video || {};
                                        const cloudflareCurrent = getCloudflareCurrentForEditor(videoObj);
                                        const cloudflareCandidate = getCloudflareCandidateForEditor(videoObj);
                                        const muxCurrent = videoObj?.providers?.mux?.current;
                                        const muxCandidate = videoObj?.providers?.mux?.candidate;
                                        const hasMuxStandby = !!(muxCurrent?.assetId || muxCandidate?.assetId);
                                        const muxState = getCourseEditorMuxState(videoObj);
                                        const controlledLessonKey = `${h.id}/${sec.id}/${it.id}`;
                                        const controlledSelected = controlledMuxLessons.some(
                                          (lesson) => lesson.key === controlledLessonKey);
                                        const videoUid =
                                          cloudflareCurrent?.assetId ||
                                          cloudflareCandidate?.assetId ||
                                          "";
                                        const hasVideo = !!videoUid;
                                        const hasDocs = docs.length > 0;
                                        const docUploading =
                                          uploadingDocKey === `${h.id}_${sec.id}_${it.id}`;
                                        const lessonKey = makeLessonKey(h.id, sec.id, it.id);
                                        const uploadInfo = videoUploadingMap[lessonKey];
                                        const videoDeleting = !!deletingVideoMap[lessonKey];
                                        const videoReady = !!cloudflareCurrent?.isReady;

                                        return (
                                          <Card
                                            key={it.id}
                                            className="border"
                                            style={{ background: "rgba(127,127,127,.03)" }}
                                          >
                                            <Card.Body>
                                              <div className="d-flex align-items-start justify-content-between gap-3">
                                                <div className="flex-grow-1">
                                                  <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                                                    <Badge bg="primary">Lesson {it.order}</Badge>

                                                    {it.contentStatus ? (
                                                      <Badge
                                                        bg="light"
                                                        text="dark"
                                                        style={{
                                                          border: "1px solid rgba(0,0,0,.12)",
                                                        }}
                                                      >
                                                        {it.contentStatus}
                                                      </Badge>
                                                    ) : null}

                                                    {hasVideo ? (
                                                      <Badge bg="info" text="dark">
                                                        มีวิดีโอ
                                                      </Badge>
                                                    ) : null}

                                                    {hasDocs ? (
                                                      <Badge bg="warning" text="dark">
                                                        เอกสาร {docs.length}
                                                      </Badge>
                                                    ) : null}

                                                    {videoObj?.status ? (
                                                      <Badge bg="dark">
                                                        {videoObj.status}
                                                      </Badge>
                                                    ) : null}
                                                  </div>

                                                  <Form.Control
                                                    defaultValue={it?.title || ""}
                                                    className="mb-2"
                                                    onBlur={(e) => {
                                                      const next = e.target.value.trim();
                                                      if (!next || next === it.title) return;
                                                      updateItemTitle(h.id, sec.id, it.id, next);
                                                    }}
                                                    placeholder="ชื่อหัวข้อการเรียน"
                                                  />

                                                  <Row className="g-2">
                                                    <Col md={6}>
                                                      <Form.Group>
                                                        <Form.Label className="small text-muted mb-1">
                                                          วันที่กำหนดปล่อย
                                                        </Form.Label>
                                                        <Form.Control
                                                          type="date"
                                                          value={releaseInputValue}
                                                          onChange={(e) =>
                                                            updateItemReleaseDate(
                                                              h.id,
                                                              sec.id,
                                                              it.id,
                                                              e.target.value
                                                            )
                                                          }
                                                        />
                                                        <div className="small text-muted mt-1">
                                                          {it?.releaseAt
                                                            ? `แสดงผล: ${formatThaiDate(it.releaseAt)}`
                                                            : "ยังไม่ได้กำหนดวัน"}
                                                        </div>
                                                      </Form.Group>
                                                    </Col>

                                                    <Col md={6}>
                                                      <div className="d-flex flex-column gap-2">
                                                        <Form.Check
                                                          type="switch"
                                                          id={`active-${it.id}`}
                                                          label="เปิดใช้งาน"
                                                          checked={!!it?.isActive}
                                                          onChange={() =>
                                                            toggleItemFlag(
                                                              h.id,
                                                              sec.id,
                                                              it.id,
                                                              "isActive",
                                                              !!it?.isActive
                                                            )
                                                          }
                                                        />
                                                        <Form.Check
                                                          type="switch"
                                                          id={`preview-${it.id}`}
                                                          label="ให้ดูตัวอย่างได้"
                                                          checked={!!it?.isPreview}
                                                          onChange={() =>
                                                            toggleItemFlag(
                                                              h.id,
                                                              sec.id,
                                                              it.id,
                                                              "isPreview",
                                                              !!it?.isPreview
                                                            )
                                                          }
                                                        />
                                                      </div>
                                                    </Col>
                                                  </Row>
                                                </div>

                                                <div className="d-flex flex-column gap-2">
                                                  <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline-danger"
                                                    onClick={() => removeItem(h.id, sec.id, it.id)}
                                                  >
                                                    ลบหัวข้อ
                                                  </Button>
                                                </div>
                                              </div>

                                              <hr />

                                              <div className="mb-3">
                                                <div className="fw-semibold mb-2">
                                                  วิดีโอ Cloudflare Stream
                                                </div>

                                                <Row className="g-2 mb-2">
                                                  <Col md={8}>
                                                    <Form.Group>
                                                      <Form.Label className="small text-muted">
                                                        อัปโหลดวิดีโอไปยัง
                                                      </Form.Label>
                                                      <Form.Select
                                                        value={videoUploadModeMap[lessonKey] || "cloudflare_only"}
                                                        disabled={videoDeleting || (
                                                          !!uploadInfo?.cloudflare?.status &&
                                                          ["preparing", "uploading"].includes(uploadInfo.cloudflare.status)
                                                        )}
                                                        onChange={(e) => setVideoUploadModeMap((prev) => ({
                                                          ...prev, [lessonKey]: e.target.value,
                                                        }))}
                                                      >
                                                        <option value="cloudflare_only">Cloudflare เท่านั้น</option>
                                                        <option value="cloudflare_and_mux">Cloudflare + Mux สำรอง</option>
                                                      </Form.Select>
                                                      {(videoUploadModeMap[lessonKey] || "cloudflare_only") ===
                                                        "cloudflare_and_mux" ? (
                                                        <div className="small text-muted mt-1">
                                                          อัปโหลดไฟล์เดียวกันไปยัง Mux เพื่อเตรียมวิดีโอสำรอง
                                                          โดยยังไม่เปิดให้ผู้เรียนใช้งาน
                                                        </div>
                                                      ) : null}
                                                    </Form.Group>
                                                  </Col>
                                                  <Col md={4} />
                                                  <Col md={8}>
                                                    <Form.Group>
                                                      <Form.Label className="small text-muted">
                                                        อัปโหลดวิดีโอขึ้น Cloudflare Stream
                                                      </Form.Label>
                                                      <Form.Control
                                                        type="file"
                                                        accept="video/*"
                                                        disabled={videoDeleting}
                                                        onChange={(e) => {
                                                          const file = e.target.files?.[0];
                                                          if (file) {
                                                            handleUploadVideoToCloudflare(
                                                              h.id,
                                                              sec.id,
                                                              it.id,
                                                              file
                                                            );
                                                            e.target.value = "";
                                                          }
                                                        }}
                                                      />
                                                      {/* <div className="small text-muted mt-1">
                                                        รองรับไฟล์ขนาดใหญ่ด้วย resumable upload (tus)
                                                      </div> */}
                                                      <div className="small text-muted mt-1">
                                                        รองรับไฟล์ .mp4
                                                      </div>
                                                    </Form.Group>
                                                  </Col>

                                                  <Col md={4}>
                                                    <Form.Group>
                                                      <Form.Label className="small text-muted">
                                                        สถานะวิดีโอ
                                                      </Form.Label>
                                                      <Form.Control
                                                        value={
                                                          cloudflareCurrent?.status ||
                                                          cloudflareCandidate?.status ||
                                                          "ยังไม่มีวิดีโอ"
                                                        }
                                                        readOnly
                                                      />
                                                    </Form.Group>
                                                  </Col>

                                                  <Col md={8}>
                                                    <Form.Group>
                                                      <Form.Label className="small text-muted">
                                                        Cloudflare Video UID
                                                      </Form.Label>
                                                      <Form.Control
                                                        value={videoUid}
                                                        readOnly
                                                        placeholder="ยังไม่มี UID"
                                                      />
                                                    </Form.Group>
                                                  </Col>

                                                  <Col md={4} className="d-flex align-items-end">
                                                    <Form.Check
                                                      type="switch"
                                                      id={`publish-${it.id}`}
                                                      label="เผยแพร่ทันที"
                                                      checked={it.contentStatus === "published"}
                                                      onChange={async (e) => {
                                                        try {
                                                          const nextPublished = e.target.checked;

                                                          if (nextPublished && !videoReady) {
                                                            setErr("ยังไม่สามารถเผยแพร่ได้ เพราะวิดีโอยังไม่พร้อมใช้งาน");
                                                            return;
                                                          }

                                                          const nextStatus = nextPublished
                                                            ? "published"
                                                            : getLessonContentStatus({
                                                                releaseAt: it?.releaseAt || null,
                                                                video: videoObj,
                                                                manualPublished: false,
                                                              });

                                                          updateLocalItem(h.id, sec.id, it.id, {
                                                            contentStatus: nextStatus,
                                                          });

                                                          await updateDoc(getItemRef(h.id, sec.id, it.id), {
                                                            contentStatus: nextStatus,
                                                            updatedAt: serverTimestamp(),
                                                          });
                                                        } catch (e2) {
                                                          console.error(e2);
                                                          setErr(e2?.message || "อัปเดตสถานะเผยแพร่ไม่สำเร็จ");
                                                          await loadStructure(courseId);
                                                        }
                                                      }}
                                                    />
                                                  </Col>
                                                </Row>

                                                <div className="d-flex gap-2 flex-wrap mb-2">
                                                  {VIDEO_DELETION_CONTROLS_VISIBLE ? <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline-danger"
                                                    disabled={videoDeleting}
                                                    onClick={() => handleStageLessonVideoDeletion(
                                                      h.id, sec.id, it.id, "all")}
                                                  >
                                                    {videoDeleting ? "กำลังลบ..." : "ลบวิดีโอทั้งหมดในบทเรียน"}
                                                  </Button> : null}
                                                  {VIDEO_DELETION_CONTROLS_VISIBLE && hasMuxStandby ? (
                                                    <Button type="button" size="sm" variant="outline-warning"
                                                      disabled={videoDeleting}
                                                      onClick={() => handleStageLessonVideoDeletion(
                                                        h.id, sec.id, it.id, "mux")}>ลบวิดีโอสำรอง Mux</Button>
                                                  ) : null}
                                                  {cloudflareCandidate?.assetId && cloudflareCurrent?.assetId ? (
                                                    <Button
                                                      type="button"
                                                      size="sm"
                                                      variant="outline-secondary"
                                                      disabled={videoDeleting}
                                                      onClick={() =>
                                                        handleDeleteVideoFromCloudflare(
                                                          h.id,
                                                          sec.id,
                                                          it.id,
                                                          cloudflareCandidate.assetId
                                                        )
                                                      }
                                                    >
                                                      ยกเลิก replacement
                                                    </Button>
                                                  ) : null}
                                                </div>

                                                {uploadInfo ? (
                                                  <div className="mt-2 d-flex flex-column gap-2">
                                                    <div>
                                                      <div className="small fw-semibold">วิดีโอหลัก — Cloudflare</div>
                                                      <div className="small text-muted mb-1">
                                                        {uploadInfo.cloudflare?.text || "กำลังเตรียม..."}
                                                      </div>
                                                      <ProgressBar
                                                        variant={uploadInfo.cloudflare?.status === "error" ? "danger" : "primary"}
                                                        now={uploadInfo.cloudflare?.progress || 0}
                                                      />
                                                    </div>
                                                    {uploadInfo.uploadMode === "cloudflare_and_mux" ? (
                                                      <div>
                                                        <div className="small fw-semibold">วิดีโอสำรอง — Mux</div>
                                                        <div className="small text-muted mb-1">
                                                          {uploadInfo.mux?.text || "กำลังเตรียม..."}
                                                        </div>
                                                        <ProgressBar
                                                          variant={uploadInfo.mux?.status === "error" ? "danger" : "secondary"}
                                                          now={uploadInfo.mux?.progress || 0}
                                                        />
                                                        {uploadInfo.mux?.status === "error" ? (
                                                          <Button type="button" size="sm" variant="outline-secondary"
                                                            className="mt-2"
                                                            onClick={() => retryMuxUpload(h.id, sec.id, it.id)}>
                                                            ลองอัปโหลดวิดีโอสำรองอีกครั้ง
                                                          </Button>
                                                        ) : null}
                                                      </div>
                                                    ) : null}
                                                  </div>
                                                ) : null}

                                                {videoReady ? (
                                                  <div className="mt-2 small fw-semibold text-success">
                                                    วิดีโอพร้อมใช้งาน
                                                  </div>
                                                ) : (
                                                  videoUid ? (
                                                    <div className="mt-2 small text-muted">
                                                      กำลังรอ Cloudflare ประมวลผล...
                                                    </div>
                                                  ) : null
                                                )}

                                                {cloudflareCandidate ? (
                                                  <div className="mt-2 small text-muted">
                                                    Replacement candidate รุ่น {cloudflareCandidate.generation}: {cloudflareCandidate.status}
                                                    {cloudflareCurrent?.assetId
                                                      ? " — วิดีโอเดิมยังคงใช้งานอยู่จนกว่าไฟล์ใหม่จะพร้อม"
                                                      : ""}
                                                  </div>
                                                ) : null}

                                                {videoObj?.fileName ? (
                                                  <div className="small text-muted mt-2">
                                                    ไฟล์ล่าสุด: {videoObj.fileName}
                                                    {videoObj?.fileSize
                                                      ? ` • ${formatBytes(videoObj.fileSize)}`
                                                      : ""}
                                                  </div>
                                                ) : null}

                                                <Row className="g-2 mt-2">
                                                  <Col md={8}>
                                                    <Form.Group>
                                                      <Form.Label className="small text-muted">
                                                        Thumbnail URL
                                                      </Form.Label>
                                                      <Form.Control
                                                        defaultValue={videoObj?.thumbnailUrl || ""}
                                                        onBlur={(e) => {
                                                          const next = e.target.value.trim();
                                                          if (next === (videoObj?.thumbnailUrl || "")) return;
                                                          updateItemVideoField(
                                                            h.id,
                                                            sec.id,
                                                            it.id,
                                                            "thumbnailUrl",
                                                            next
                                                          );
                                                        }}
                                                        placeholder="https://..."
                                                      />
                                                    </Form.Group>
                                                  </Col>

                                                  <Col md={4}>
                                                    <Form.Group>
                                                      <Form.Label className="small text-muted">
                                                        Duration (sec)
                                                      </Form.Label>
                                                      <Form.Control
                                                        type="number"
                                                        defaultValue={videoObj?.durationSec || 0}
                                                        onBlur={(e) => {
                                                          const next = Number(e.target.value || 0);
                                                          if (next === Number(videoObj?.durationSec || 0)) return;
                                                          updateItemVideoField(
                                                            h.id,
                                                            sec.id,
                                                            it.id,
                                                            "durationSec",
                                                            next
                                                          );
                                                        }}
                                                      />
                                                    </Form.Group>
                                                  </Col>
                                                </Row>

                                                <div className="mt-3 p-2 border rounded bg-light">
                                                  <div className="fw-semibold small">Mux Warm Standby</div>
                                                  <Badge bg={getMuxStandbyVariant(videoObj)}>
                                                    {getMuxStandbyStatus(videoObj)}
                                                  </Badge>
                                                  {muxState === COURSE_EDITOR_MUX_STATE.MISSING ? (
                                                    <Form.Check className="mt-2" type="checkbox"
                                                      id={`controlled-mux-${it.id}`}
                                                      label="เลือกสำหรับ Controlled Mux batch"
                                                      checked={controlledSelected}
                                                      disabled={controlledMuxBusy || courseDeletionActive ||
                                                        (!controlledSelected && controlledMuxLessons.length >=
                                                          CONTROLLED_MUX_BATCH_LIMIT)}
                                                      onChange={() => setControlledMuxLessons((selected) =>
                                                        toggleControlledMuxLessonSelection(selected, {
                                                          headerId: h.id, sectionId: sec.id,
                                                          itemId: it.id, title: it.title,
                                                        }))} />
                                                  ) : null}
                                                </div>
                                              </div>

                                              <div>
                                                <div className="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
                                                  <div className="fw-semibold">เอกสารประกอบ</div>

                                                  <div style={{ minWidth: 260 }}>
                                                    <Form.Control
                                                      type="file"
                                                      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,image/*"
                                                      onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                          handleUploadDocument(h.id, sec.id, it.id, file);
                                                          e.target.value = "";
                                                        }
                                                      }}
                                                    />
                                                  </div>
                                                </div>

                                                {docUploading ? (
                                                  <div className="small text-muted mb-2">
                                                    <Spinner animation="border" size="sm" /> กำลังอัปโหลดเอกสาร...
                                                  </div>
                                                ) : null}

                                                {docs.length === 0 ? (
                                                  <div className="text-muted small">ยังไม่มีเอกสารแนบ</div>
                                                ) : (
                                                  <div className="d-flex flex-column gap-2">
                                                    {docs.map((docFile, docIndex) => (
                                                      <div
                                                        key={`${it.id}-doc-${docIndex}`}
                                                        className="d-flex justify-content-between align-items-center gap-3"
                                                        style={{
                                                          padding: "10px 12px",
                                                          border: "1px solid rgba(0,0,0,.12)",
                                                          borderRadius: 10,
                                                          background: "#fff",
                                                        }}
                                                      >
                                                        <div className="min-w-0">
                                                          <div className="fw-semibold text-break">
                                                            {docFile.name}
                                                          </div>
                                                          <div className="small text-muted">
                                                            {docFile.ext?.toUpperCase() || "FILE"}
                                                            {typeof docFile.size === "number"
                                                              ? ` • ${(docFile.size / 1024 / 1024).toFixed(2)} MB`
                                                              : ""}
                                                          </div>
                                                        </div>

                                                        <div className="d-flex gap-2">
                                                          {docFile.fileUrl ? (
                                                            <a
                                                              href={docFile.fileUrl}
                                                              target="_blank"
                                                              rel="noreferrer"
                                                              className="btn btn-sm btn-outline-secondary"
                                                            >
                                                              เปิดดู
                                                            </a>
                                                          ) : null}

                                                          <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline-danger"
                                                            onClick={() =>
                                                              removeDocumentFromItem(
                                                                h.id,
                                                                sec.id,
                                                                it.id,
                                                                docIndex
                                                              )
                                                            }
                                                          >
                                                            ลบเอกสาร
                                                          </Button>
                                                        </div>
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            </Card.Body>
                                          </Card>
                                        );
                                      })
                                    )}
                                    </div>
                                  ) : (
                                    <div className="text-muted small mt-3">
                                      ซ่อนอยู่ • {safeArray(sec.items).length} lessons
                                    </div>
                                  )}
                                </Card.Body>
                              </Card>
                              );
                            })
                          )}
                        </div>
                        ) : (
                          <div className="text-muted small mt-3">
                            ซ่อนอยู่ • {safeArray(h.sections).length} sections
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                    );
                  })}
                </div>
              )}

              <Card className="border mt-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="fw-semibold">เอกสารดาวน์โหลดของคอร์ส</div>
                    <Button variant="outline-primary" size="sm" onClick={addCourseDownloadRow}>
                      + เพิ่มเอกสาร
                    </Button>
                  </div>

                  {!isEdit ? (
                    <Alert variant="info" className="mb-0">
                      กรุณาบันทึกคอร์สก่อน แล้วจึงอัปโหลดเอกสารดาวน์โหลดของคอร์สได้
                    </Alert>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {safeArray(courseDownloads)
                        .filter((item) => !SPECIAL_COURSE_DOWNLOAD_SCOPES.has(item?.scope))
                        .map((docItem) => (
                        <div
                          key={docItem.id}
                          className="p-3 rounded"
                          style={{ border: "1px solid rgba(0,0,0,.12)", background: "#fff" }}
                        >
                          <Row className="g-2">
                            <Col md={8}>
                              <Form.Group>
                                <Form.Label>ประเภทเอกสาร</Form.Label>
                                <Form.Select
                                  value={docItem.scope || "general"}
                                  onChange={(e) =>
                                    updateCourseDownloadRow(docItem.id, { scope: e.target.value })
                                  }
                                >
                                  {COURSE_DOWNLOAD_SCOPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>

                            <Col md={8}>
                              <Form.Group>
                                <Form.Label>ชื่อเอกสาร</Form.Label>
                                <Form.Control
                                  value={docItem.name || ""}
                                  onChange={(e) =>
                                    updateCourseDownloadRowLocal(docItem.id, { name: e.target.value })
                                  }
                                  onBlur={(e) =>
                                    updateCourseDownloadRow(docItem.id, { name: e.target.value })
                                  }
                                  placeholder="เช่น เอกสาร คอร์สเนติบัณฑิต ขาแพ่ง"
                                />
                              </Form.Group>
                            </Col>

                            
                          </Row>
                          <Row className="g-2 mt-1">
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label>อัปโหลดไฟล์ PDF</Form.Label>
                                <Form.Control
                                  type="file"
                                  accept=".pdf,application/pdf"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleUploadCourseDownload(docItem.id, file);
                                      e.target.value = "";
                                    }
                                  }}
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          {uploadingCourseDocId === docItem.id ? (
                            <div className="small text-muted mt-2">
                              <Spinner animation="border" size="sm" /> กำลังอัปโหลดเอกสาร...
                            </div>
                          ) : null}

                          {docItem.fileUrl ? (
                            <div className="d-flex justify-content-between align-items-center mt-3 gap-2 flex-wrap">
                              <div className="small text-muted">
                                {docItem.fileName || docItem.name}
                                {docItem.size ? ` • ${formatBytes(docItem.size)}` : ""}
                              </div>

                              <div className="d-flex gap-2">
                                <a
                                  href={docItem.fileUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-sm btn-outline-secondary"
                                >
                                  เปิดดู
                                </a>

                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => removeCourseDownloadRow(docItem.id)}
                                >
                                  ลบเอกสาร
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex justify-content-end mt-3">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline-danger"
                                onClick={() => removeCourseDownloadRow(docItem.id)}
                              >
                                ลบฟอร์มนี้
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                      {safeArray(courseDownloads).filter(
                        (item) => !SPECIAL_COURSE_DOWNLOAD_SCOPES.has(item?.scope)
                      ).length > 1 ? (
                        <div className="d-flex justify-content-end pt-1">
                          <Button
                            type="button"
                            variant="outline-primary"
                            size="sm"
                            onClick={addCourseDownloadRow}
                          >
                            + เพิ่มเอกสาร
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className="border">
            <Card.Body>
              <Form onSubmit={onSaveCourse}>
                <Form.Group className="mb-3">
                  <Form.Label>ชื่อคอร์ส (title)</Form.Label>
                  <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>คำอธิบายใต้ชื่อ (subTitle)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={subTitle}
                    onChange={(e) => setSubTitle(e.target.value)}
                    placeholder="เช่น คอร์สเนติบัณฑิต 1/79 ประกอบไปด้วยคอร์สยาวและคอร์สสรุป..."
                  />
                </Form.Group>

                <Row className="g-2">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>ประเภทคอร์ส</Form.Label>
                      <Form.Select
                        value={courseType}
                        onChange={(e) => setCourseType(e.target.value)}
                        disabled={lockCourseTypeAndSlug}
                      >
                        {COURSE_TYPE_OPTIONS.map((x) => (
                          <option key={x.value} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </Form.Select>
                      {/* <div className="text-muted small mt-1">
                        ถ้าเลือก “เนติบัณฑิต” เวลาเพิ่ม Header จะให้เลือก headerType
                      </div> */}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Slug (ชื่อ URL คอร์ส)</Form.Label>
                      <Form.Control
                        value={slug}
                        onChange={(e) => setSlug(e.target.value)}
                        disabled={lockCourseTypeAndSlug}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>หมวดหมู่ (categories)</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {CATEGORY_OPTIONS.map((c) => {
                      const active = categories.includes(c);
                      return (
                        <Badge
                          key={c}
                          bg={active ? "primary" : "light"}
                          text={active ? "white" : "dark"}
                          role="button"
                          style={{
                            cursor: "pointer",
                            border: "1px solid rgba(0,0,0,.12)",
                          }}
                          onClick={() => toggleCategory(c)}
                        >
                          {c}
                        </Badge>
                      );
                    })}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>คำอธิบายคอร์ส (courseDescriptions)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={courseDescriptions}
                    onChange={(e) => setCourseDescriptions(e.target.value)}
                    placeholder="เช่น คอร์สสอนออนไลน์เนติบัณฑิต 1/79 โดยติวเตอร์มืออาชีพ พร้อมเอกสารประกอบ..."
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>สิ่งที่จะได้รับ (ใส่บรรทัดละ 1 ข้อ)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={receiveText}
                    onChange={(e) => setReceiveText(e.target.value)}
                    placeholder={"วิดีโอ 12 บทเรียน\nEbook ฟรีในการสอน\nเข้าเรียนได้ตลอด 24 ชั่วโมง"}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>ไฮไลต์คอร์ส (ใส่บรรทัดละ 1 ข้อ)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={highlightsText}
                    onChange={(e) => setHighlightsText(e.target.value)}
                    placeholder={"คอร์สยาว อธิบายพร้อมฎีกา\nคอร์สสรุป สกัดจากคำบรรยาย\nพร้อมเทคนิคการทำข้อสอบ"}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>ภาพปกแนวนอน (อัปโหลด)</Form.Label>

                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setCoverFile(file);
                        handleUploadCover(file);
                      }
                    }}
                  />

                  {coverFile ? (
                    <div className="small text-muted mt-2">ไฟล์ล่าสุด: {coverFile.name}</div>
                  ) : null}

                  {uploadingCover && (
                    <div className="text-muted small mt-2">กำลังอัปโหลด...</div>
                  )}

                  {imageCoverUrl && (
                    <div className="mt-3">
                      <img
                        src={imageCoverUrl}
                        alt="cover"
                        style={{
                          width: "100%",
                          maxHeight: 220,
                          objectFit: "cover",
                          borderRadius: 10,
                          border: "1px solid rgba(0,0,0,.12)",
                        }}
                      />
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>วิดีโอโฆษณา/ตัวอย่าง (videoAdsUrl)</Form.Label>
                  <Form.Control
                    value={videoAdsUrl}
                    onChange={(e) => setVideoAdsUrl(e.target.value)}
                    placeholder="ลิงก์ YouTube/Facebook หรืออื่น ๆ"
                  />
                </Form.Group>

                <Card className="border mb-3">
                  <Card.Body>
                    <div className="fw-semibold mb-3">QR กลุ่มติว</div>

                    {!isEdit ? (
                      <Alert variant="info" className="mb-0">
                        กรุณาบันทึกคอร์สก่อน แล้วจึงอัปโหลด QR กลุ่มติวได้
                      </Alert>
                    ) : (
                      <>
                        <Form.Check
                          className="mb-3"
                          type="switch"
                          id="lineGroupQrActive"
                          label="แสดง QR กลุ่มติวในหน้าคอร์ส"
                          checked={lineGroupQrImage?.isActive !== false}
                          onChange={(e) =>
                            setLineGroupQrImage((prev) => ({
                              ...(prev || {}),
                              isActive: e.target.checked,
                            }))
                          }
                        />

                        <Form.Group>
                          <Form.Label>อัปโหลดรูป QR กลุ่มติว</Form.Label>
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleUploadLineGroupQr(file);
                                e.target.value = "";
                              }
                            }}
                          />
                        </Form.Group>

                        {uploadingLineGroupQr ? (
                          <div className="small text-muted mt-2">
                            <Spinner animation="border" size="sm" /> กำลังอัปโหลด QR...
                          </div>
                        ) : null}

                        {lineGroupQrImage?.imageUrl ? (
                          <div className="mt-3">
                            <img
                              src={lineGroupQrImage.imageUrl}
                              alt="QR กลุ่มติว"
                              style={{
                                width: "100%",
                                maxWidth: 260,
                                borderRadius: 10,
                                border: "1px solid rgba(0,0,0,.12)",
                              }}
                            />

                            <div className="small text-muted mt-2">
                              {lineGroupQrImage.fileName || "QR กลุ่มติว"}
                              {lineGroupQrImage.size
                                ? ` • ${formatBytes(lineGroupQrImage.size)}`
                                : ""}
                            </div>

                            <div className="d-flex gap-2 mt-2">
                              <a
                                href={lineGroupQrImage.imageUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-sm btn-outline-secondary"
                              >
                                เปิดดู
                              </a>

                              <Button
                                type="button"
                                size="sm"
                                variant="outline-danger"
                                onClick={removeLineGroupQr}
                              >
                                ลบ QR
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-muted small mt-2">ยังไม่มี QR กลุ่มติว</div>
                        )}
                      </>
                    )}
                  </Card.Body>
                </Card>

                {/* <Row className="g-2">
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>ระดับ (level)</Form.Label>
                      <Form.Select value={level} onChange={(e) => setLevel(e.target.value)}>
                        {LEVEL_OPTIONS.map((x) => (
                          <option key={x} value={x}>
                            {x}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row> */}

                <Card className="border mb-3">
                  <Card.Body>
                    <div className="fw-semibold mb-3">ตารางปฏิทินเรียน</div>

                    <Form.Check
                      className="mb-3"
                      type="switch"
                      id="showCourseCalendarImages"
                      label="แสดงตารางปฏิทินเรียนในหน้าแรก"
                      checked={showCourseCalendarImages}
                      onChange={(e) => setShowCourseCalendarImages(e.target.checked)}
                    />

                    <div className="d-flex flex-column gap-3">
                      {safeArray(courseCalendarImages).map((img) => (
                        <div key={img.id} className="p-3 rounded" style={{ border: "1px solid rgba(0,0,0,.12)", background: "#fff" }}>
                          <Form.Group className="mb-2">
                            <Form.Label>ชื่อรูป</Form.Label>
                            <Form.Control
                              value={img.name || ""}
                              onChange={(e) => updateCourseImageRowLocal("calendar", img.id, { name: e.target.value })}
                              placeholder="เช่น ปฏิทินเรียนรอบ 1/79"
                            />
                          </Form.Group>

                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleUploadCourseImage("calendar", img.id, file);
                                e.target.value = "";
                              }
                            }}
                          />

                          {uploadingCourseImageKey === `calendar_${img.id}` ? (
                            <div className="small text-muted mt-2">
                              <Spinner animation="border" size="sm" /> กำลังอัปโหลดรูปภาพ...
                            </div>
                          ) : null}

                          {img.imageUrl ? (
                            <div className="mt-3">
                              <img src={img.imageUrl} alt={img.name || "calendar"} style={{ width: "100%", borderRadius: 10 }} />
                            </div>
                          ) : null}

                          <div className="d-flex justify-content-end mt-2">
                            <Button size="sm" variant="outline-danger" onClick={() => removeCourseImageRow("calendar", img.id)}>
                              ลบรูปนี้
                            </Button>
                          </div>
                        </div>
                      ))}

                      <div className="d-flex justify-content-end">
                        <Button type="button" variant="outline-primary" size="sm" onClick={() => addCourseImageRow("calendar")}>
                          + เพิ่มรูปปฏิทิน
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                <Card className="border mb-3">
                  <Card.Body>
                    <div className="fw-semibold mb-3">รูปภาพตารางเรียน</div>

                    <Form.Check
                      className="mb-3"
                      type="switch"
                      id="showCourseScheduleImages"
                      label="แสดงรูปภาพตารางเรียนในหน้าแรก"
                      checked={showCourseScheduleImages}
                      onChange={(e) => setShowCourseScheduleImages(e.target.checked)}
                    />

                    <div className="d-flex flex-column gap-3">
                      {safeArray(courseScheduleImages).map((img) => (
                        <div key={img.id} className="p-3 rounded" style={{ border: "1px solid rgba(0,0,0,.12)", background: "#fff" }}>
                          <Form.Group className="mb-2">
                            <Form.Label>ชื่อรูป</Form.Label>
                            <Form.Control
                              value={img.name || ""}
                              onChange={(e) => updateCourseImageRowLocal("schedule", img.id, { name: e.target.value })}
                              placeholder="เช่น ตารางเรียนแพ่ง / ตารางเรียนอาญา"
                            />
                          </Form.Group>

                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleUploadCourseImage("schedule", img.id, file);
                                e.target.value = "";
                              }
                            }}
                          />

                          {uploadingCourseImageKey === `schedule_${img.id}` ? (
                            <div className="small text-muted mt-2">
                              <Spinner animation="border" size="sm" /> กำลังอัปโหลดรูปภาพ...
                            </div>
                          ) : null}

                          {img.imageUrl ? (
                            <div className="mt-3">
                              <img src={img.imageUrl} alt={img.name || "schedule"} style={{ width: "100%", borderRadius: 10 }} />
                            </div>
                          ) : null}

                          <div className="d-flex justify-content-end mt-2">
                            <Button size="sm" variant="outline-danger" onClick={() => removeCourseImageRow("schedule", img.id)}>
                              ลบรูปนี้
                            </Button>
                          </div>
                        </div>
                      ))}

                      <div className="d-flex justify-content-end">
                        <Button type="button" variant="outline-primary" size="sm" onClick={() => addCourseImageRow("schedule")}>
                          + เพิ่มรูปตารางเรียน
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                <Card className="border mb-3">
                  <Card.Body>
                    <div className="fw-semibold mb-3">รูปภาพ Checklist การเรียน</div>

                    <Form.Check
                      className="mb-3"
                      type="switch"
                      id="showCourseChecklistImages"
                      label="แสดงรูปภาพ Checklist ในหน้าติว"
                      checked={showCourseChecklistImages}
                      onChange={(e) => setShowCourseChecklistImages(e.target.checked)}
                    />

                    <div className="d-flex flex-column gap-3">
                      {safeArray(courseChecklistImages).map((img) => (
                        <div
                          key={img.id}
                          className="p-3 rounded"
                          style={{ border: "1px solid rgba(0,0,0,.12)", background: "#fff" }}
                        >
                          <Form.Group className="mb-2">
                            <Form.Label>ชื่อรูป</Form.Label>
                            <Form.Control
                              value={img.name || ""}
                              onChange={(e) =>
                                updateCourseImageRowLocal("checklist", img.id, {
                                  name: e.target.value,
                                })
                              }
                              placeholder="เช่น Checklist การเรียนครบถ้วน 1/79"
                            />
                          </Form.Group>

                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleUploadCourseImage("checklist", img.id, file);
                                e.target.value = "";
                              }
                            }}
                          />

                          {uploadingCourseImageKey === `checklist_${img.id}` ? (
                            <div className="small text-muted mt-2">
                              <Spinner animation="border" size="sm" /> กำลังอัปโหลดรูปภาพ...
                            </div>
                          ) : null}

                          {img.imageUrl ? (
                            <div className="mt-3">
                              <img
                                src={img.imageUrl}
                                alt={img.name || "checklist"}
                                style={{ width: "100%", borderRadius: 10 }}
                              />
                            </div>
                          ) : null}

                          <div className="d-flex justify-content-end mt-2">
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => removeCourseImageRow("checklist", img.id)}
                            >
                              ลบรูปนี้
                            </Button>
                          </div>
                        </div>
                      ))}

                      <div className="d-flex justify-content-end">
                        <Button
                          type="button"
                          variant="outline-primary"
                          size="sm"
                          onClick={() => addCourseImageRow("checklist")}
                        >
                          + เพิ่มรูป Checklist
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                {courseType === "thaibar" ? (
                  <Card className="border mb-3">
                    <Card.Body>
                      <div className="fw-semibold mb-3">
                        มาตราสำคัญสำหรับคอร์สเนติบัณฑิต
                      </div>

                      <div className="d-flex flex-column gap-3">
                        {SPECIAL_COURSE_DOWNLOADS.map((config) => {
                          const item = getSpecialCourseDownload(courseDownloads, config.scope);
                          const isUploading = uploadingCourseDocId === config.scope;

                          return (
                            <div
                              key={config.scope}
                              className="p-3 rounded"
                              style={{ border: "1px solid rgba(0,0,0,.12)", background: "#fff" }}
                            >
                              <div className="fw-semibold mb-2">{config.heading}</div>
                              <Form.Control
                                type="file"
                                accept=".pdf,application/pdf"
                                disabled={!isEdit || isUploading}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleUploadSpecialCourseDownload(config, file);
                                  e.target.value = "";
                                }}
                              />

                              {!isEdit ? (
                                <div className="small text-muted mt-2">
                                  กรุณาบันทึกคอร์สก่อน แล้วจึงอัปโหลดเอกสารได้
                                </div>
                              ) : null}

                              {isUploading ? (
                                <div className="small text-muted mt-2">
                                  <Spinner animation="border" size="sm" /> กำลังอัปโหลดเอกสาร...
                                </div>
                              ) : null}

                              {item?.fileUrl ? (
                                <div className="d-flex justify-content-between align-items-center mt-3 gap-2 flex-wrap">
                                  <div className="small text-muted">
                                    {item.fileName || item.name}
                                    {item.size ? ` • ${formatBytes(item.size)}` : ""}
                                  </div>
                                  <div className="d-flex gap-2">
                                    <a
                                      href={item.fileUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="btn btn-sm btn-outline-secondary"
                                    >
                                      เปิดดู
                                    </a>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => removeSpecialCourseDownload(config)}
                                    >
                                      ลบไฟล์
                                    </Button>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </Card.Body>
                  </Card>
                ) : null}

                {courseType === "thaibar" ? (
                <Card className="border mb-3">
                  <Card.Body>
                    <div className="fw-semibold mb-3">ราคาแพ็กเกจคอร์สเนติบัณฑิต</div>

                    <div className="d-flex flex-column gap-3">
                      {THAIBAR_PACKAGE_GROUPS.map((group) => (
                        <div
                          key={group.key}
                          className="p-3 rounded"
                          style={{
                            border: "1px solid rgba(0,0,0,.12)",
                            background: "#fff",
                          }}
                        >
                          <div className="fw-semibold mb-3">{group.title}</div>

                          <div className="d-flex flex-column gap-3">
                            {group.items.map((pkgValue) => (
                              <div
                                key={pkgValue}
                                className="p-3 rounded"
                                style={{
                                  border: "1px solid rgba(0,0,0,.10)",
                                  background: "#fafafa",
                                }}
                              >
                                <div className="fw-semibold mb-2">
                                  {getThaibarPackageLabel(pkgValue)}
                                </div>

                                <Row className="g-2">
                                  <Col md={6}>
                                    <Form.Group>
                                      <Form.Label>ราคา (ตัวเลข)</Form.Label>
                                      <Form.Control
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={packagePrices?.[pkgValue]?.price ?? ""}
                                        onChange={(e) => {
                                          const onlyDigits = e.target.value.replace(/\D/g, "");
                                          updatePackagePriceField(pkgValue, "price", onlyDigits);
                                        }}
                                        placeholder="เช่น 6990"
                                      />
                                    </Form.Group>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Group>
                                      <Form.Label>ข้อความราคา</Form.Label>
                                      <Form.Control
                                        value={packagePrices?.[pkgValue]?.priceText ?? ""}
                                        onChange={(e) =>
                                          updatePackagePriceField(pkgValue, "priceText", e.target.value)
                                        }
                                        placeholder='เช่น "6,990.-"'
                                      />
                                    </Form.Group>
                                  </Col>
                                </Row>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              ) : (
                <>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ราคา (ตัวเลข)</Form.Label>
                        <Form.Control
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={price}
                          onChange={(e) => {
                            const onlyDigits = e.target.value.replace(/\D/g, "");
                            setPrice(onlyDigits);
                          }}
                          placeholder="เช่น 6990"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ข้อความราคา</Form.Label>
                        <Form.Control
                          value={priceText}
                          onChange={(e) => setPriceText(e.target.value)}
                          placeholder='เช่น "6,990.-"'
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}

              <Card className="border mb-3">
                <Card.Body>
                  <div className="fw-semibold mb-3">ตัวเลือกการชำระเงิน</div>

                  <Form.Check
                    className="mb-3"
                    type="switch"
                    id="allowInstallment"
                    label="เปิดใช้งานการผ่อนชำระ"
                    checked={allowInstallment}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAllowInstallment(checked);

                      if (checked && (!maxInstallments || Number(maxInstallments) < 2)) {
                        setMaxInstallments(3);
                      }

                      if (!checked) {
                        setInstallmentPackageKeys([]);
                      }
                    }}
                  />

                  {allowInstallment ? (
                    <Row className="g-2">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>จำนวนงวดสูงสุด</Form.Label>
                          <Form.Control
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={maxInstallments}
                            onChange={(e) => {
                              const onlyDigits = e.target.value.replace(/\D/g, "");
                              setMaxInstallments(onlyDigits);
                            }}
                            placeholder="เช่น 3"
                          />
                          <div className="text-muted small mt-1">
                            หน้าทำรายการชำระเงินจะมีตัวเลือกการผ่อนชำระเพิ่ม
                            {/* เมื่อเปิดใช้งาน หน้า CoursePayment.jsx จะสามารถแสดงตัวเลือก
                            [ชำระเต็มจำนวน] และ [ผ่อนชำระ] ได้ภายหลัง */}
                          </div>
                        </Form.Group>
                        {courseType === "thaibar" ? (
                          <div className="mt-3">
                            <div className="fw-semibold mb-2">
                              แพ็กเกจที่อนุญาตให้ผ่อนชำระ
                            </div>

                            <div className="d-flex flex-column gap-2">
                              {THAIBAR_PACKAGE_OPTIONS.map((pkg) => {
                                const checked = safeArray(installmentPackageKeys).includes(pkg.value);
                                const pkgPrice = Number(packagePrices?.[pkg.value]?.price || 0);

                                return (
                                  <Form.Check
                                    key={pkg.value}
                                    type="checkbox"
                                    id={`installment-pkg-${pkg.value}`}
                                    label={`${pkg.label}${pkgPrice ? ` • ${pkgPrice.toLocaleString("th-TH")} บาท` : ""}`}
                                    checked={checked}
                                    disabled={pkgPrice <= 0}
                                    onChange={() => toggleInstallmentPackageKey(pkg.value)}
                                  />
                                );
                              })}
                            </div>

                            <div className="text-muted small mt-2">
                              ระบบจะแสดงตัวเลือกผ่อนชำระเฉพาะแพ็กเกจที่เลือกไว้เท่านั้น
                            </div>
                          </div>
                        ) : null}
                      </Col>
                    </Row>
                  ) : (
                    <div className="text-muted small">
                      ใช้วิธีการชำระเต็มจำนวนเพียงวิธีเดียว
                    </div>
                  )}
                </Card.Body>
              </Card>

              <Card className="border mb-3">
                <Card.Body>
                  <div className="fw-semibold mb-3">Affiliate</div>

                  <Form.Check
                    className="mb-3"
                    type="switch"
                    id="affiliateEnabled"
                    label="เปิดใช้งาน Affiliate สำหรับคอร์สนี้"
                    checked={affiliateEnabled}
                    onChange={(e) => setAffiliateEnabled(e.target.checked)}
                  />

                  <Form.Group>
                    <Form.Label>ค่าคอมมิชชัน (%)</Form.Label>
                    <Form.Control
                      type="text"
                      inputMode="decimal"
                      value={affiliateCommissionPercent}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^\d.]/g, "");
                        setAffiliateCommissionPercent(cleaned);
                      }}
                      onBlur={() =>
                        setAffiliateCommissionPercent((prev) =>
                          normalizeAffiliateCommissionPercent(
                            prev,
                            DEFAULT_AFFILIATE_COURSE_SETTINGS.affiliateCommissionPercent
                          )
                        )
                      }
                      disabled={!affiliateEnabled}
                      placeholder="เช่น 10"
                    />
                    <div className="text-muted small mt-1">
                      Phase 1 บันทึกเฉพาะการตั้งค่า ยังไม่มีการติดตาม referral หรือสร้างค่าคอมมิชชัน
                    </div>
                  </Form.Group>
                </Card.Body>
              </Card>

                <Form.Check
                  className="mb-2"
                  type="switch"
                  id="isActive"
                  // label="แสดงผลขาย (isActive)"
                  label="แสดงผลขาย"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />

                <Form.Check
                  className="mb-3"
                  type="switch"
                  id="isFeatured"
                  // label="คอร์สแนะนำ (isFeatured)"
                  label="คอร์สแนะนำ"
                  checked={isFeatured}
                  onChange={(e) => setIsFeatured(e.target.checked)}
                />
              </Form>
              <CourseReviewSettings courseId={isEdit ? courseId : null} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {VIDEO_DELETION_CONTROLS_VISIBLE ? <Modal show={showCourseVideoDeletionModal}
        onHide={() => !courseVideoDeletionBusy && setShowCourseVideoDeletionModal(false)} centered>
        <Modal.Header closeButton={!courseVideoDeletionBusy}>
          <Modal.Title>ลบวิดีโอทั้งหมดในคอร์ส</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>การดำเนินการนี้จะลบวิดีโอทั้งหมดในคอร์สนี้ ทั้งวิดีโอหลัก Cloudflare และวิดีโอสำรอง Mux</p>
          <p>บทเรียน โครงสร้างคอร์ส การลงทะเบียน และประวัติความคืบหน้าของผู้เรียนจะไม่ถูกลบ</p>
          <Alert variant="danger">การลบวิดีโอไม่สามารถย้อนกลับได้</Alert>
          <div className="small mb-3">พบวิดีโอในคอร์ส: Cloudflare {courseVideoInventory.cloudflare} · Mux {courseVideoInventory.mux} · บทเรียนที่มีวิดีโอ {courseVideoInventory.lessons}</div>
          <Form.Label>กรุณาพิมพ์ DELETE เพื่อยืนยัน</Form.Label>
          <Form.Control value={courseVideoDeletionConfirmation} autoComplete="off"
            onChange={(event) => setCourseVideoDeletionConfirmation(event.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={courseVideoDeletionBusy}
            onClick={() => setShowCourseVideoDeletionModal(false)}>ยกเลิก</Button>
          <Button variant="danger" disabled={courseVideoDeletionBusy ||
            !isExactCourseVideoDeleteConfirmation(courseVideoDeletionConfirmation)}
            onClick={stageCourseVideoDelete}>
            {courseVideoDeletionBusy ? "กำลังเริ่ม..." : "ลบวิดีโอทั้งหมด"}
          </Button>
        </Modal.Footer>
      </Modal> : null}

      <Modal show={showHeaderModal} onHide={closeAddHeaderModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>เพิ่ม Header</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {courseType === "thaibar" ? (
            <Form.Group className="mb-3">
              <Form.Label>ประเภท Header</Form.Label>
              <Form.Select
                value={pendingHeaderType}
                onChange={(e) => setPendingHeaderType(e.target.value)}
              >
                <option value="civil">เนติบัณฑิต - ยาวแพ่ง/วิแพ่ง</option>
                <option value="criminal">เนติบัณฑิต - ยาวอาญา/วิอาญา</option>
                <option value="summary_civil">เนติบัณฑิต - สรุปแพ่ง/วิแพ่ง</option>
                <option value="summary_criminal">เนติบัณฑิต - สรุปอาญา/วิอาญา</option>
              </Form.Select>
              <div className="small text-muted mt-1">
                ระบบจะบันทึกค่า value ลง Firestore เพื่อใช้กำหนดสิทธิการแสดงผลหน้าติว
              </div>
            </Form.Group>
          ) : null}

          <Form.Group>
            <Form.Label>ชื่อ Header</Form.Label>
            <Form.Control
              autoFocus
              value={pendingHeaderTitle}
              onChange={(e) => {
                setHeaderTitleTouched(true);
                setPendingHeaderTitle(e.target.value);
              }}
              placeholder={
                courseType === "thaibar"
                  ? "เช่น เนติบัณฑิตขาแพ่ง, สรุปขาอาญา"
                  : "เช่น ตะลุยข้อสอบชุดที่ 1"
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitAddHeader();
                }
              }}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeAddHeaderModal}>
            ยกเลิก
          </Button>
          <Button variant="primary" onClick={submitAddHeader}>
            บันทึก Header
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSectionModal} onHide={closeAddSectionModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>เพิ่ม Section</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>ชื่อ Section</Form.Label>
            <Form.Control
              autoFocus
              value={pendingSectionTitle}
              onChange={(e) => setPendingSectionTitle(e.target.value)}
              placeholder="เช่น ข้อ 1 บรรพ 3, ข้อ 2 บรรพ 4"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitAddSection();
                }
              }}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeAddSectionModal}>
            ยกเลิก
          </Button>
          <Button variant="primary" onClick={submitAddSection}>
            บันทึก Section
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showLessonModal} onHide={closeAddLessonModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>เพิ่มหัวข้อการเรียน</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>ชื่อหัวข้อการเรียน</Form.Label>
            <Form.Control
              autoFocus
              value={pendingLessonTitle}
              onChange={(e) => setPendingLessonTitle(e.target.value)}
              placeholder="เช่น แพ่ง ข้อ 1 EP. 1, สรุปแพ่ง ข้อ 2 EP. 2"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitAddLesson();
                }
              }}
            />
            <div className="small text-muted mt-1">
              ระบบจะสร้าง lesson ใหม่ใน section ที่เลือกไว้
            </div>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeAddLessonModal}>
            ยกเลิก
          </Button>
          <Button variant="primary" onClick={submitAddLesson}>
            บันทึกหัวข้อการเรียน
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCurriculumModal}
        onHide={closeCurriculumPreview}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>โครงสร้างคอร์ส</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <h2 className="h5 mt-1">โครงบทเรียน</h2>

          {curriculumPreviewData.length === 0 ? (
            <div className="text-muted">ยังไม่มีโครงสร้างการเรียน</div>
          ) : (
            <div className="cc-curr">
              {curriculumPreviewData.map((h, hi) => {
                const headerKey = String(hi);
                const headerOpen = openHeaderKey === headerKey;

                return (
                  <div key={headerKey}>
                    <button
                      type="button"
                      className={`cc-row ${headerOpen ? "is-open" : ""}`}
                      onClick={() => {
                        if (headerOpen) {
                          setOpenHeaderKey("");
                        } else {
                          setOpenHeaderKey(headerKey);
                          setOpenSectionKey(`${hi}-0`);
                        }
                      }}
                    >
                      <div>
                        <div className="cc-title">
                          {hi + 1}. {h?.headerTitle || "Header"}
                        </div>
                        <div className="cc-sub">
                          {safeArray(h?.sections).length} sections
                        </div>
                      </div>

                      <span className="cc-chevron">⌄</span>
                    </button>

                    <div className="cc-divider" />

                    {!headerOpen && safeArray(h?.sections).length > 0 && (
                      <div className="cc-preview">
                        {safeArray(h.sections).map((s, si) => {
                          const secKey = `${hi}-${si}`;
                          const isActive = openSectionKey === secKey;

                          return (
                            <div
                              key={`${secKey}-preview`}
                              className={`cc-prev-item ${isActive ? "is-active" : ""}`}
                              role="button"
                              tabIndex={0}
                              onClick={() => openHeaderAndSection(hi, si)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") openHeaderAndSection(hi, si);
                              }}
                            >
                              <div>
                                <div className="cc-prev-title">
                                  {si + 1}. {s?.title || "Section"}
                                </div>
                                <div className="cc-sub">
                                  {safeArray(s?.topics).length} lessons
                                </div>
                              </div>

                              <span className="cc-chevron">⌄</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className={`cc-panel ${headerOpen ? "is-open" : ""}`}>
                      <div className="cc-panel-inner">
                        {safeArray(h?.sections).length === 0 ? (
                          <div className="text-muted small">ยังไม่มี section</div>
                        ) : (
                          safeArray(h.sections).map((s, si) => {
                            const secKey = `${hi}-${si}`;
                            const secOpen = openSectionKey === secKey;

                            return (
                              <div className="cc-sec" key={secKey}>
                                <button
                                  type="button"
                                  className={`cc-secbtn ${secOpen ? "is-active" : ""}`}
                                  onClick={() => {
                                    setOpenHeaderKey(headerKey);
                                    setOpenSectionKey(secKey);
                                  }}
                                >
                                  <div>
                                    <div className="cc-title" style={{ fontSize: 14 }}>
                                      {si + 1}. {s?.title || "Section"}
                                    </div>
                                    <div className="cc-sub">
                                      {safeArray(s?.topics).length} lessons
                                    </div>
                                  </div>
                                  <span
                                    className="cc-chevron"
                                    style={{ transform: secOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                                  >
                                    ⌄
                                  </span>
                                </button>

                                <div className={`cc-secbody ${secOpen ? "is-open" : ""}`}>
                                  <div className="cc-secbody-inner">
                                    {safeArray(s?.topics).length === 0 ? (
                                      <div className="text-muted small">ยังไม่มีหัวข้อ</div>
                                    ) : (
                                      <ul className="cc-topic">
                                        {safeArray(s.topics).map((t, ti) => (
                                          <li key={ti}>
                                            <div className="d-flex align-items-start gap-2 min-w-0">
                                              <span
                                                style={{
                                                  width: 10,
                                                  height: 10,
                                                  borderRadius: "50%",
                                                  flex: "0 0 auto",
                                                  marginTop: 7.5,
                                                  ...getLessonBulletStyle(t?.bulletStatus),
                                                }}
                                              />

                                              <div className="min-w-0 w-100">
                                                <div className="d-flex justify-content-between align-items-center gap-2">
                                                  <span className="text-break">{t?.title || "-"}</span>

                                                  {t?.releaseDateText ? (
                                                    <span className="text-muted small text-nowrap">
                                                      {t.releaseDateText}
                                                    </span>
                                                  ) : (
                                                    <span className="text-muted small">—</span>
                                                  )}
                                                </div>

                                                {t?.hasPdfDocument ? (
                                                  <div className="small text-muted mt-1">
                                                    แนบเอกสารแล้ว
                                                  </div>
                                                ) : null}
                                              </div>
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="small text-muted me-auto">
            สีสถานะ: เทา = ยังไม่มีวิดีโอ, ส้ม = พร้อมแล้วรอวันเผยแพร่, เขียว = เผยแพร่แล้ว
          </div>
          <Button variant="secondary" onClick={closeCurriculumPreview}>
            ปิด
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
